import { useState, useRef, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { rentalApi } from "@/lib/api";
import { printTicket } from "@/lib/ticketGenerator";
import MixedPaymentInput from "@/components/MixedPaymentInput";
import axios from "axios";
import { useScannerListener } from "@/hooks/useScannerListener";
import { useSettings } from "@/contexts/SettingsContext";
import { loadPacksAndTariffs as loadPacksAndTariffsFn, calculateTariffPrice, getPackPrice } from "@/lib/tariffCalculator";
import { requireCashSession } from "@/lib/cashSessionCheck";
import { 
  RotateCcw, 
  Check, 
  X, 
  AlertTriangle, 
  Loader2,
  Barcode,
  User,
  Calendar,
  DollarSign,
  Phone,
  RefreshCcw,
  Banknote,
  Zap,
  Mail,
  MapPin,
  IdCard,
  MessageCircle,
  ExternalLink,
  Package,
  ArrowLeftRight,
  ArrowRight,
  Scan,
  CreditCard,
  CheckCircle,
  Printer,
  CalendarPlus,
  Lock,
  AlertCircle,
  Clock,
  CheckCheck,
  ArrowDownToLine,
  Wrench,
  Search,
  Users,
  PackageCheck,
  Radio,
  Minus,
  Plus
} from "lucide-react";
import { toast } from "sonner";
import { formatCurrency, getCurrencySymbol } from "@/lib/currency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENT_METHODS = [
  { value: "cash", label: "Efectivo" },
  { value: "card", label: "Tarjeta" },
  { value: "transfer", label: "Transferencia" },
];

export default function Returns() {
  const settingsCtx = useSettings();
  const [barcodeInput, setBarcodeInput] = useState("");
  const [rental, setRental] = useState(null);
  const [scannedBarcodes, setScannedBarcodes] = useState([]);

  // ============ DATE WARNING MODAL STATE ============
  const [dateWarningModal, setDateWarningModal] = useState(false);
  const [dateWarningData, setDateWarningData] = useState(null);
  // dateWarningData = { itemName, expectedDate, isEarly, addAction }
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [pendingReturns, setPendingReturns] = useState({ today: [], other_days: [] });
  
  // ============ PARTIAL RETURN STATE (for generic items with quantity > 1) ============
  // Map: barcode -> quantity to return
  const [returnQuantities, setReturnQuantities] = useState({});
  
  // Refund dialog state
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [refundDays, setRefundDays] = useState(1);
  const [refundAmount, setRefundAmount] = useState(0);
  const [refundMethod, setRefundMethod] = useState("cash");
  const [refundReason, setRefundReason] = useState("");
  const [processingRefund, setProcessingRefund] = useState(false);
  
  // ============ SETTLEMENT MODAL STATE (Liquidación) ============
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [settlementData, setSettlementData] = useState({
    balance: 0,           // + cliente debe, - hay que devolver
    daysUsed: 0,
    daysPaid: 0,
    pricePerDay: 0,
    serviceUsed: 0,       // Precio real del servicio usado
    totalPaid: 0,         // Lo que pagó inicialmente
    itemsToReturn: [],
    paymentMethod: "cash",
    originalPaymentMethod: "cash"
  });
  const [settlementProcessing, setSettlementProcessing] = useState(false);
  // Mixed payment state for settlement cobro (balance > 0)
  const [settlementMixedPayment, setSettlementMixedPayment] = useState({ cashAmount: 0, cardAmount: 0, method: "cash", change: 0, isValid: false, mode: "cash" });
  
  // NUEVO: Opción de anulación total / devolución completa
  const [fullRefundOverride, setFullRefundOverride] = useState(false);
  
  // ============ GESTIÓN DE DEPÓSITOS ============
  const [depositAction, setDepositAction] = useState("return"); // "return" or "forfeit"
  const [forfeitReason, setForfeitReason] = useState("");
  const [showDepositDialog, setShowDepositDialog] = useState(false);
  
  // Customer modal state
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(false);
  
  // ============ GESTIONAR CAMBIO MODAL STATE ============
  const [changeModal, setChangeModal] = useState(false);
  const [changeRental, setChangeRental] = useState(null);
  const [changeItems, setChangeItems] = useState([]); // Array of items with swap info
  const [activeSwapIndex, setActiveSwapIndex] = useState(null);
  const [changeNewBarcode, setChangeNewBarcode] = useState("");
  
  // Packs y tarifas para cálculos correctos
  const [storePacks, setStorePacks] = useState([]);
  const [storeTariffs, setStoreTariffs] = useState([]);
  
  // Financial tracking
  const [timeDelta, setTimeDelta] = useState(0); // + extension, - reduction
  const [materialDelta, setMaterialDelta] = useState(0); // + upgrade, - downgrade
  const [totalDelta, setTotalDelta] = useState(0); // Net total
  
  // ============ HISTORIAL DE DEVOLUCIONES RECIENTES (Undo feature) ============
  const [recentReturns, setRecentReturns] = useState([]);
  const [loadingRecentReturns, setLoadingRecentReturns] = useState(false);
  const [undoingReturn, setUndoingReturn] = useState(null); // rental_id being undone
  const [showUndoConfirmDialog, setShowUndoConfirmDialog] = useState(false);
  const [pendingUndoRental, setPendingUndoRental] = useState(null); // rental waiting for cash confirmation
  
  // Day adjustment
  const [originalDays, setOriginalDays] = useState(0);
  const [newDays, setNewDays] = useState(0);
  const [adjustDays, setAdjustDays] = useState(false);
  
  // Additional change modal state
  const [changeAdjustDate, setChangeAdjustDate] = useState(false);
  const [changeNewEndDate, setChangeNewEndDate] = useState("");
  const [changeDaysRemaining, setChangeDaysRemaining] = useState(0);
  const [changeOriginalDays, setChangeOriginalDays] = useState(0);
  const [changeNewTotalDays, setChangeNewTotalDays] = useState(0);
  const [changeDateDelta, setChangeDateDelta] = useState(0);
  const [changeTotalDelta, setChangeTotalDelta] = useState(0);
  
  const [changePaymentMethod, setChangePaymentMethod] = useState("cash");
  const [changeLoading, setChangeLoading] = useState(false);
  const [changeComplete, setChangeComplete] = useState(false);
  const changeInputRef = useRef(null);
  
  // Estado para días a descontar (no facturables) en devolución
  const [discountDays, setDiscountDays] = useState(0);
  const [discountCredit, setDiscountCredit] = useState(0);
  
  const barcodeRef = useRef(null);

  // ============ HORA DE GRACIA PARA DEVOLUCIONES MATUTINAS ============
  const [graceHour, setGraceHour] = useState("10:00");
  const [savingGraceHour, setSavingGraceHour] = useState(false);

  // Cargar packs y tarifas cuando se identifica un alquiler
  useEffect(() => {
    if (rental) {
      loadPacksAndTariffsFn().then(({ packs, tariffs }) => {
        setStorePacks(packs);
        setStoreTariffs(tariffs);
      });
    }
  }, [rental?.id]);

  // ============ PARTIAL RETURN HELPERS (Generic Items) ============
  
  // Get the quantity to return for a generic item (default: full quantity)
  const getReturnQuantity = (item) => {
    if (!item.is_generic || (item.quantity || 1) <= 1) return 1;
    const qty = item.quantity || 1;
    const alreadyReturned = item.returned_quantity || 0;
    const pending = qty - alreadyReturned;
    // Return the stored value or default to pending (full return)
    return returnQuantities[item.barcode] ?? pending;
  };
  
  // Set the quantity to return for a generic item
  const setReturnQuantity = (barcode, quantity, maxQty) => {
    const validQty = Math.max(0, Math.min(quantity, maxQty));
    setReturnQuantities(prev => ({
      ...prev,
      [barcode]: validQty
    }));
    
    // If quantity is 0, remove from scannedBarcodes
    // If quantity > 0, add to scannedBarcodes
    if (validQty === 0) {
      setScannedBarcodes(prev => prev.filter(code => code !== barcode));
    } else if (!scannedBarcodes.includes(barcode)) {
      setScannedBarcodes(prev => [...prev, barcode]);
    }
  };
  
  // Get pending quantity for a generic item
  const getPendingQuantity = (item) => {
    if (!item.is_generic) return 1;
    const qty = item.quantity || 1;
    const alreadyReturned = item.returned_quantity || 0;
    return qty - alreadyReturned;
  };
  
  // Check if item is a generic with partial return capability
  const isPartialReturnItem = (item) => {
    return item.is_generic && (item.quantity || 1) > 1;
  };

  // ============ DATE VALIDATION HELPER ============
  // Returns true if date matches today (silent add), false if mismatch (show warning)
  const checkDateAndAdd = (item, itemBarcode, successMessage, addFn) => {
    if (!rental?.end_date) {
      // No date available - add silently
      addFn();
      return;
    }
    const expectedDate = new Date(rental.end_date);
    expectedDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (today.getTime() === expectedDate.getTime()) {
      // Date matches today - silent add, no friction
      addFn();
    } else {
      // Date mismatch - intercept and show warning
      setDateWarningData({
        itemName: item?.item_type || item?.brand || 'artículo',
        expectedDate,
        isEarly: today < expectedDate,
        addAction: () => {
          addFn();
          if (successMessage) toast.success(successMessage);
        }
      });
      setDateWarningModal(true);
    }
  };

  const confirmDateWarning = () => {
    if (dateWarningData?.addAction) {
      dateWarningData.addAction();
    }
    setDateWarningModal(false);
    setDateWarningData(null);
    refocusBarcodeInput();
  };

  const dismissDateWarning = () => {
    setDateWarningModal(false);
    setDateWarningData(null);
    refocusBarcodeInput();
  };

  // ============ GLOBAL SCANNER LISTENER (HID BARCODE READER) ============
  // Captura entrada de lectores HID como Netum NT-1698W cuando el cursor no está en un input
  const handleGlobalScan = useCallback(async (scannedCode) => {
    console.log('[SCANNER] Global scan detected in Returns:', scannedCode);
    
    // If change modal is open, let its own handler manage the scan
    if (changeModal) {
      setChangeNewBarcode(scannedCode);
      // Trigger the change modal's barcode handler
      return;
    }
    
    // Set the barcode input and trigger search
    setBarcodeInput(scannedCode);
    
    // Simulate the barcode scan logic
    if (!rental) {
      // No rental loaded - search for rental by barcode
      setLoading(true);
      try {
        const response = await rentalApi.getByBarcode(scannedCode);
        setRental(response.data);
        toast.success(`Alquiler encontrado: ${response.data.customer_name}`);
        setBarcodeInput("");
        
        // DATE VALIDATION on first HID scan (inline: rental state not yet updated)
        const rentalData = response.data;
        const codeUpper = scannedCode.toUpperCase();
        const matchedItem = (rentalData.items || []).find(i =>
          (i.barcode && i.barcode.toUpperCase() === codeUpper) ||
          (i.internal_code && i.internal_code.toUpperCase() === codeUpper) ||
          (i.item_id && i.item_id === scannedCode)
        );
        
        if (matchedItem && rentalData.end_date) {
          const expectedDate = new Date(rentalData.end_date);
          expectedDate.setHours(0, 0, 0, 0);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          if (today.getTime() === expectedDate.getTime()) {
            setScannedBarcodes([scannedCode]);
          } else {
            setDateWarningData({
              itemName: matchedItem.item_type || matchedItem.brand || 'artículo',
              expectedDate,
              isEarly: today < expectedDate,
              addAction: () => setScannedBarcodes([scannedCode])
            });
            setDateWarningModal(true);
          }
        } else {
          setScannedBarcodes([scannedCode]);
        }
      } catch (error) {
        toast.error("No se encontró alquiler activo para este artículo");
        setBarcodeInput("");
      } finally {
        setLoading(false);
      }
    } else {
      // Rental loaded - mark item for return
      const codeUpper = scannedCode.toUpperCase();
      const item = rental.items.find(i => 
        (i.barcode && i.barcode.toUpperCase() === codeUpper) ||
        (i.internal_code && i.internal_code.toUpperCase() === codeUpper) ||
        (i.item_id && i.item_id === scannedCode)
      );
      
      if (!item) {
        toast.error("Este artículo no pertenece a este alquiler");
        setBarcodeInput("");
        return;
      }
      
      if (item.returned) {
        toast.info("Este artículo ya fue devuelto");
        setBarcodeInput("");
        return;
      }
      
      // Toggle logic
      const itemBarcode = item.barcode || scannedCode;
      const isAlreadyScanned = scannedBarcodes.some(code => 
        code.toUpperCase() === (item.barcode || "").toUpperCase() ||
        code.toUpperCase() === (item.internal_code || "").toUpperCase() ||
        code === item.item_id
      );
      
      if (isAlreadyScanned) {
        setScannedBarcodes(prev => prev.filter(code => 
          code.toUpperCase() !== (item.barcode || "").toUpperCase() &&
          code.toUpperCase() !== (item.internal_code || "").toUpperCase() &&
          code !== item.item_id
        ));
        toast.info(`${item.item_type || item.brand} desmarcado`);
      } else {
        // DATE VALIDATION: check if today matches the expected return date
        const addFn = () => setScannedBarcodes(prev => [...prev, itemBarcode]);
        checkDateAndAdd(item, itemBarcode, `${item.item_type || item.brand} marcado ✓`, addFn);
      }
      setBarcodeInput("");
    }
  }, [rental, scannedBarcodes, changeModal]);
  
  const { isScanning: globalScannerActive, forceFocus: focusBarcodeInput } = useScannerListener({
    onScan: handleGlobalScan,
    inputRef: barcodeRef,
    enabled: settingsCtx.quickScanMode !== false && !changeModal && !showCustomerModal && !showSettlementModal && !showRefundDialog && !dateWarningModal,
    minLength: 3,
    maxTimeBetweenKeys: 50,
    scannerDetectionThreshold: 4,
    autoFocus: true, // Auto-focus on barcode input
  });

  // ============ GESTIONAR CAMBIO FUNCTIONS ============
  
  // Función para obtener el código interno de un item por su barcode
  const enrichItemsWithInternalCode = async (items) => {
    const barcodes = items.map(i => i.barcode).filter(Boolean);
    if (barcodes.length === 0) return items;
    
    try {
      // Buscar los items por sus barcodes para obtener internal_code
      const response = await axios.get(`${API}/items/by-barcodes`, {
        params: { barcodes: barcodes.join(',') },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const itemsMap = {};
      (response.data || []).forEach(item => {
        itemsMap[item.barcode] = item;
      });
      
      // Enriquecer cada item con su internal_code
      return items.map(item => ({
        ...item,
        internal_code: itemsMap[item.barcode]?.internal_code || item.internal_code || ''
      }));
    } catch (error) {
      console.error('Error enriching items with internal_code:', error);
      return items;
    }
  };
  
  const openChangeModal = async (rental) => {
    const rentalDays = rental.days || 1;
    
    // Get all pending items from the rental
    let pendingItems = (rental.pending_items || rental.items || [])
      .filter(i => !i.returned)
      .map((item, idx) => ({
        ...item,
        originalIndex: idx,
        swapNewItem: null,
        swapDelta: 0,
        isSwapping: false,
        swapType: null // 'upgrade', 'downgrade', 'technical'
      }));
    
    // Enriquecer items con internal_code
    pendingItems = await enrichItemsWithInternalCode(pendingItems);
    
    // Calculate REAL days remaining: End Date - Today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = rental.end_date ? new Date(rental.end_date) : new Date();
    endDate.setHours(0, 0, 0, 0);
    const msPerDay = 24 * 60 * 60 * 1000;
    const daysRemaining = Math.ceil((endDate - today) / msPerDay);
    
    setChangeRental({
      ...rental,
      days: rentalDays,
      pricePerDay: rental.total_amount / rentalDays
    });
    setChangeItems(pendingItems);
    setActiveSwapIndex(null);
    setChangeNewBarcode("");
    
    // Reset financial tracking
    setTimeDelta(0);
    setMaterialDelta(0);
    setTotalDelta(0);
    
    // Reset discount days
    setDiscountDays(0);
    setDiscountCredit(0);
    
    // Day settings - Real calculation
    setOriginalDays(rentalDays);
    setNewDays(rentalDays);
    setAdjustDays(false);
    
    // Set days remaining correctly
    setChangeDaysRemaining(daysRemaining);
    setChangeOriginalDays(rentalDays);
    setChangeNewTotalDays(rentalDays);
    setChangeNewEndDate(rental.end_date ? rental.end_date.split('T')[0] : "");
    setChangeAdjustDate(false);
    setChangeDateDelta(0);
    setChangeTotalDelta(0);
    
    setChangeComplete(false);
    // SECURITY: Pre-select original payment method for refunds
    setChangePaymentMethod(rental.payment_method || "cash");
    setChangeModal(true);
  };

  const closeChangeModal = () => {
    setChangeModal(false);
    setChangeRental(null);
    setChangeItems([]);
    setActiveSwapIndex(null);
    setTimeDelta(0);
    setMaterialDelta(0);
    setTotalDelta(0);
    setChangeComplete(false);
    setDiscountDays(0);
    setDiscountCredit(0);
    // Auto-focus barcode input after closing modal
    refocusBarcodeInput();
  };

  // Handle date adjustment — TARIFF-BASED (not flat rate)
  const handleDateAdjustment = (newEndDateStr) => {
    if (!changeRental || !newEndDateStr) return;

    setChangeNewEndDate(newEndDateStr);

    // Calculate new total days from original start date to new end date
    const startDate = new Date(changeRental.start_date);
    startDate.setHours(0, 0, 0, 0);
    const newEndDate = new Date(newEndDateStr);
    newEndDate.setHours(0, 0, 0, 0);
    const msPerDay = 24 * 60 * 60 * 1000;

    const newTotalDays = Math.ceil((newEndDate - startDate) / msPerDay) + 1; // +1 includes both start and end day

    // Tariff-based delta replaces simple flat-rate multiplication
    const allItemTypes = (changeRental.items || []).filter(i => !i.returned).map(i => i.item_type);
    const tariffOld = calculateTariffPrice(allItemTypes, changeOriginalDays, storePacks, storeTariffs);
    const tariffNew = calculateTariffPrice(allItemTypes, newTotalDays, storePacks, storeTariffs);

    let dateDelta;
    if (tariffOld > 0) {
      const discountRatio = (changeRental.total_amount || 0) / tariffOld;
      dateDelta = Math.round((tariffNew - tariffOld) * discountRatio * 100) / 100;
    } else {
      // Fallback: proporcional si no hay tarifa configurada
      const pricePerDay = changeRental.pricePerDay || 0;
      dateDelta = (newTotalDays - changeOriginalDays) * pricePerDay;
    }

    setChangeNewTotalDays(newTotalDays);
    setChangeDateDelta(dateDelta);

    // Update days remaining
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const newDaysRemaining = Math.ceil((newEndDate - today) / msPerDay);
    setChangeDaysRemaining(newDaysRemaining);

    // Recalculate total with material delta
    recalculateChangeTotalDelta(dateDelta, materialDelta);
  };

  // Recalculate total from date + material changes
  const recalculateChangeTotalDelta = (dateDelta, materialDelta) => {
    setChangeTotalDelta(dateDelta + materialDelta);
  };

  // Recalculate total from time + material
  const recalculateTotal = (time, material) => {
    setTotalDelta(time + material);
  };

  // Recalculate material delta from all items
  const recalculateMaterialDelta = (items) => {
    const total = items.reduce((sum, item) => {
      if (item.isSwapping && item.swapDelta) {
        return sum + item.swapDelta;
      }
      return sum;
    }, 0);
    setMaterialDelta(total);
    recalculateTotal(timeDelta, total);
    // Also update the change total delta with date delta
    recalculateChangeTotalDelta(changeDateDelta, total);
  };

  // Start swap mode for item
  const startItemSwap = (index) => {
    setActiveSwapIndex(index);
    setChangeNewBarcode("");
    setTimeout(() => changeInputRef.current?.focus(), 100);
  };

  // Cancel swap for item
  const cancelItemSwap = (index) => {
    const updated = [...changeItems];
    updated[index] = {
      ...updated[index],
      swapNewItem: null,
      swapDelta: 0,
      isSwapping: false,
      swapType: null
    };
    setChangeItems(updated);
    setActiveSwapIndex(null);
    recalculateMaterialDelta(updated);
  };

  // Search and assign new item for swap
  const searchSwapItem = async (code) => {
    if (!code.trim() || activeSwapIndex === null || !changeRental) return;
    
    setChangeLoading(true);
    try {
      const response = await axios.get(`${API}/items?search=${code}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      const items = response.data;
      const foundItem = items.find(i => 
        i.barcode?.toUpperCase() === code.toUpperCase() || 
        i.internal_code?.toUpperCase() === code.toUpperCase()
      );

      if (!foundItem) {
        toast.error(`No se encontró artículo "${code}"`);
        return;
      }

      if (foundItem.status === 'rented') {
        toast.error("Este artículo ya está alquilado");
        return;
      }

      if (!['available', 'dirty'].includes(foundItem.status)) {
        toast.error(`Artículo no disponible (${foundItem.status})`);
        return;
      }

      // Calculate price delta for this swap
      const oldItem = changeItems[activeSwapIndex];
      const delta = await calculateItemSwapDelta(oldItem, foundItem);
      
      // Determine swap type
      let swapType = 'technical';
      if (delta > 0) swapType = 'upgrade';
      else if (delta < 0) swapType = 'downgrade';

      // Update the item
      const updated = [...changeItems];
      updated[activeSwapIndex] = {
        ...updated[activeSwapIndex],
        swapNewItem: foundItem,
        swapDelta: delta,
        isSwapping: true,
        swapType
      };
      setChangeItems(updated);
      
      const swapTypeLabel = swapType === 'upgrade' ? '⬆️ Upgrade' : 
                           swapType === 'downgrade' ? '⬇️ Downgrade' : 
                           '🔄 Cambio técnico';
      toast.success(`${swapTypeLabel}: ${oldItem.internal_code || oldItem.barcode} → ${foundItem.internal_code || foundItem.barcode}`);
      
      setActiveSwapIndex(null);
      setChangeNewBarcode("");
      recalculateMaterialDelta(updated);

    } catch (error) {
      toast.error("Error al buscar artículo");
    } finally {
      setChangeLoading(false);
    }
  };

  // Calculate delta for single item swap using tariffs
  const calculateItemSwapDelta = async (oldItem, newItem) => {
    try {
      // CRITICAL FIX: If both items are the same type, delta is 0 (size/technical swap)
      // This prevents incorrect charges when swapping items of the same category (e.g., different boot sizes)
      const oldType = oldItem.item_type?.toLowerCase()?.trim();
      const newType = newItem.item_type?.toLowerCase()?.trim();
      
      if (oldType === newType) {
        // Same item type = technical swap (size change, etc.) = no price difference
        console.log(`[Swap] Same type detected: "${oldType}" - Delta: 0.00`);
        return 0;
      }
      
      // Different types - calculate price difference using tariffs
      const tariffsRes = await axios.get(`${API}/tariffs`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const tariffs = tariffsRes.data;
      
      // Calculate for remaining days
      const daysToCharge = Math.max(1, newDays - (originalDays - (changeRental?.days || originalDays)));
      const dayField = daysToCharge <= 10 ? `day_${daysToCharge}` : 'day_11_plus';

      const oldTariff = tariffs.find(t => t.item_type?.toLowerCase() === oldType);
      const oldPrice = oldTariff?.[dayField] || oldItem.unit_price || 0;

      const newTariff = tariffs.find(t => t.item_type?.toLowerCase() === newType);
      const newPrice = newTariff?.[dayField] || newItem.rental_price || 0;

      console.log(`[Swap] Different types: "${oldType}" (${formatCurrency(oldPrice)}) → "${newType}" (${formatCurrency(newPrice)}) - Delta: ${formatCurrency(newPrice - oldPrice)}`);
      return newPrice - oldPrice;
    } catch (error) {
      console.error('[Swap] Error calculating delta:', error);
      return 0;
    }
  };

  // Execute all changes
  const executeAllChanges = async () => {
    if (!changeRental) {
      toast.error("Error: No hay contrato seleccionado");
      return;
    }

    const itemsToSwap = changeItems.filter(i => i.isSwapping && i.swapNewItem);
    const hasDateChange = changeAdjustDate && changeDateDelta !== 0;

    if (itemsToSwap.length === 0 && !hasDateChange) {
      toast.error("No hay cambios que procesar");
      return;
    }

    setChangeLoading(true);
    try {
      // Process each material swap
      for (const item of itemsToSwap) {
        await axios.post(`${API}/rentals/${changeRental.id}/central-swap`, {
          old_item_barcode: item.barcode || item.internal_code,
          new_item_barcode: item.swapNewItem.barcode || item.swapNewItem.internal_code,
          days_remaining: changeNewTotalDays,
          payment_method: changePaymentMethod,
          delta_amount: item.swapDelta || 0
        }, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
      }

      // Process date change if needed
      if (hasDateChange) {
        await axios.patch(`${API}/rentals/${changeRental.id}/modify-duration`, {
          new_days: changeNewTotalDays,
          new_end_date: changeNewEndDate,
          new_total: changeRental.total_amount + changeDateDelta,
          payment_method: changePaymentMethod,
          difference_amount: changeDateDelta
        }, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
      }

      setChangeComplete(true);
      toast.success(`✅ Regularización completada`);
      loadPendingReturns();

    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al procesar los cambios");
    } finally {
      setChangeLoading(false);
    }
  };

  // ============ IMPRIMIR TICKET DE REGULARIZACIÓN (USA GENERADOR MAESTRO) ============
  const printRegularizationTicket = () => {
    if (!changeRental) return;
    
    const itemsSwapped = changeItems.filter(i => i.isSwapping && i.swapNewItem);
    const hasDateChange = changeAdjustDate && changeDateDelta !== 0;
    
    // Preparar datos para el generador maestro
    const ticketData = {
      operationNumber: changeRental.operation_number,
      date: new Date().toISOString(),
      customer: changeRental.customer_name,
      dni: changeRental.customer_dni,
      contractId: changeRental.id,
      // Items cambiados
      oldItems: itemsSwapped.map(item => ({
        name: `${item.internal_code || item.barcode} - ${item.item_type}`,
        item_type: item.item_type
      })),
      newItems: itemsSwapped.map(item => ({
        name: `${item.swapNewItem.internal_code || item.swapNewItem.item_type} - ${item.swapNewItem.item_type}`,
        item_type: item.swapNewItem.item_type
      })),
      // Ajuste de fechas
      dateAdjustment: hasDateChange,
      originalDays: changeOriginalDays,
      newDays: changeNewTotalDays,
      daysDelta: changeDateDelta,
      // Totales
      materialDelta: materialDelta,
      difference: changeTotalDelta,
      paymentMethod: changePaymentMethod
    };
    
    // Usar generador maestro — ruta unificada via iframe
    printTicket({
      ticketType: 'swap',
      data: ticketData
    }).catch(err => {
      console.error('[Print] Error al imprimir ticket de regularización:', err);
      toast.error("No se pudo abrir ventana de impresión. Permite los popups.");
    });
  };

  // Print ticket for all changes - Uses centralized template
  const printMultiChangeTicket = () => {
    printRegularizationTicket();
  };

  const quickReturn = async (rentalId, customerName) => {
    // NUEVO FLUJO: Cargar contrato en el "Mostrador de Recepción" para check-in
    setLoading(true);
    try {
      // Cargar el alquiler completo para mostrarlo en la zona activa
      const response = await axios.get(`${API}/rentals/${rentalId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      const rentalData = response.data;
      setRental(rentalData);
      setScannedBarcodes([]); // Reset escaneados
      
      toast.info(`📋 Contrato cargado: ${customerName}`, {
        description: `${rentalData.items?.filter(i => !i.returned).length || 0} artículos pendientes de escanear`
      });
      
      // Focus en el campo de escaneo
      if (barcodeRef.current) {
        barcodeRef.current.focus();
        barcodeRef.current.select();
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cargar el contrato");
    } finally {
      setLoading(false);
    }
  };
  
  // ============ SETTLEMENT CALCULATION (Liquidación) ============
  // REGLA ATÓMICA: Cada artículo usa su propio item_days para determinar
  // si la devolución es en fecha, anticipada o tardía.
  // - Pagado + anticipado → reembolso de días no usados
  // - Pagado + en fecha/tarde → 0 o recargo por días extra
  // - Pendiente → cobrar saldo proporcional
  const calculateSettlement = () => {
    if (!rental || toReturnItems.length === 0) return null;
    
    const now = new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(rental.start_date);
    startDate.setHours(0, 0, 0, 0);

    const msPerDay = 24 * 60 * 60 * 1000;
    const rentalDays = rental.days || 1;
    
    const [graceH, graceM] = graceHour.split(':').map(Number);
    const isBeforeGrace = (now.getHours() * 60 + now.getMinutes()) < (graceH * 60 + (graceM || 0));
    
    const actualPaidAmount = rental.paid_amount || 0;
    const pendingAmountTotal = rental.pending_amount || 0;
    const totalAmount = rental.total_amount || 0;
    const isFullyPaid = pendingAmountTotal === 0 && actualPaidAmount > 0;
    const isFullyPendingRental = actualPaidAmount === 0 && pendingAmountTotal > 0;
    
    const allPendingItems = rental.items.filter(i => !i.returned);
    const priceOfAllPending = allPendingItems.reduce((s, i) => s + (i.unit_price || 0), 0);
    const priceOfReturned = toReturnItems.reduce((s, i) => s + (i.unit_price || 0), 0);
    const priceRatioDisplay = priceOfAllPending > 0 ? priceOfReturned / priceOfAllPending : 1;

    // Calcular balance con soporte completo para packs e ítems individuales
    let totalBalance = 0;
    let reasons = [];
    let itemBreakdowns = []; // structured breakdown for UI display
    let overallDaysUsed = rentalDays;
    let overallDaysPaid = rentalDays;

    // ── Helpers de días para un ítem ──────────────────────────────────────────
    const calcDaysInfo = (itemDaysPaid) => {
      const itemEndDate = new Date(startDate);
      itemEndDate.setDate(startDate.getDate() + itemDaysPaid);
      itemEndDate.setHours(0, 0, 0, 0);
      let rawOverdue = Math.max(0, Math.ceil((today - itemEndDate) / msPerDay));
      if (isBeforeGrace && rawOverdue > 0) rawOverdue -= 1;
      const daysOverdue = Math.max(0, rawOverdue);
      let daysUsed;
      if (daysOverdue > 0) {
        daysUsed = itemDaysPaid + daysOverdue;
      } else {
        const daysFromStart = Math.max(1, Math.ceil((today - startDate) / msPerDay) + 1);
        daysUsed = Math.min(daysFromStart, itemDaysPaid);
      }
      return { daysOverdue, daysUsed };
    };

    // ── Calcular surcharge correcto (individual o fallback proporcional) ──────
    const calcSurcharge = (itemTypes, daysUsed, daysPaid, daysOverdue, unitPriceTotal) => {
      const tariffNew = calculateTariffPrice(itemTypes, daysUsed, storePacks, storeTariffs);
      const tariffOld = calculateTariffPrice(itemTypes, daysPaid, storePacks, storeTariffs);
      if (tariffOld > 0) {
        // Preservar ratio de descuento aplicado en el alquiler original
        const discountRatio = unitPriceTotal > 0 ? unitPriceTotal / tariffOld : 1;
        return Math.max(0, (tariffNew - tariffOld) * discountRatio);
      }
      // Fallback proporcional (tarifa no configurada para este tipo)
      if (unitPriceTotal > 0 && daysPaid > 0) {
        return (unitPriceTotal / daysPaid) * daysOverdue;
      }
      return 0;
    };

    // ── Calcular reembolso por anticipada ────────────────────────────────────
    const calcRefund = (itemTypes, daysPaid, daysUsed, unitPriceTotal) => {
      const tariffOld = calculateTariffPrice(itemTypes, daysPaid, storePacks, storeTariffs);
      const tariffNew = calculateTariffPrice(itemTypes, daysUsed, storePacks, storeTariffs);
      if (tariffOld > 0) {
        // Preservar ratio de descuento aplicado en el alquiler original
        const discountRatio = unitPriceTotal > 0 ? unitPriceTotal / tariffOld : 1;
        return Math.max(0, (tariffOld - tariffNew) * discountRatio);
      }
      // Fallback proporcional (tarifa no configurada para este tipo)
      if (unitPriceTotal > 0 && daysPaid > 0) {
        const unusedDays = daysPaid - daysUsed;
        return (unitPriceTotal / daysPaid) * unusedDays;
      }
      return 0;
    };

    // ── Agrupar ítems por pack_instance_id ───────────────────────────────────
    const processedPackInstances = new Set();
    const processedBarcodes = new Set();

    // FASE 1: procesar grupos de pack
    for (const item of toReturnItems) {
      const instanceId = item.pack_instance_id;
      if (!instanceId || processedPackInstances.has(instanceId)) continue;
      processedPackInstances.add(instanceId);

      // Recoger TODOS los ítems del pack en toReturnItems
      const packItems = toReturnItems.filter(i => i.pack_instance_id === instanceId);
      packItems.forEach(i => processedBarcodes.add(i.barcode));

      const itemDaysPaid = packItems[0].item_days || rentalDays;
      const packTypes = packItems.map(i => i.item_type);
      const packUnitTotal = packItems.reduce((s, i) => s + (i.unit_price || 0), 0);
      const { daysOverdue, daysUsed } = calcDaysInfo(itemDaysPaid);

      if (item === toReturnItems[0]) {
        overallDaysUsed = daysUsed;
        overallDaysPaid = itemDaysPaid;
      }

      let groupBalance = 0;
      let groupReason = "";
      const tag = `Pack(${instanceId.slice(-4)})`;

      if (daysOverdue > 0) {
        // PACK-FAITHFUL: use specific pack tariff for overdue surcharge
        const packId = packItems[0].pack_id;
        const specificPack = packId ? storePacks.find(p => p.id === packId) : null;
        let packLabel;
        if (specificPack) {
          const pricePaid = getPackPrice(specificPack, itemDaysPaid);
          const priceOverdue = getPackPrice(specificPack, daysUsed);
          const discountRatio = pricePaid > 0 ? packUnitTotal / pricePaid : 1;
          const usedCost = Math.round(priceOverdue * discountRatio * 100) / 100;
          groupBalance = Math.max(0, usedCost - packUnitTotal);
          packLabel = `${specificPack.name}`;
        } else {
          groupBalance = calcSurcharge(packTypes, daysUsed, itemDaysPaid, daysOverdue, packUnitTotal);
          packLabel = `Pack · ${packItems.map(i => i.item_type).join(' + ')}`;
        }
        groupReason = `${packLabel}: +${daysOverdue}d extra (${itemDaysPaid}d→${daysUsed}d: +${formatCurrency(groupBalance)})`;
        itemBreakdowns.push({ label: packLabel, type: 'overdue', daysPaid: itemDaysPaid, daysUsed, daysOverdue, amount: groupBalance });
      } else if (daysUsed < itemDaysPaid) {
        const unusedDays = itemDaysPaid - daysUsed;

        // PACK-FAITHFUL: look up specific pack by pack_id for correct refund calculation
        const packId = packItems[0].pack_id;
        const specificPack = packId ? storePacks.find(p => p.id === packId) : null;
        let refund;
        let packLabel;
        if (specificPack) {
          // Use the EXACT pack tariff tier for days paid and days used
          const pricePaid = getPackPrice(specificPack, itemDaysPaid);
          const priceUsed = getPackPrice(specificPack, daysUsed);
          // Preserve original discount ratio (if pack was sold with manual discount)
          const discountRatio = pricePaid > 0 ? packUnitTotal / pricePaid : 1;
          const usedCost = Math.round(priceUsed * discountRatio * 100) / 100;
          refund = Math.max(0, packUnitTotal - usedCost);
          packLabel = `${specificPack.name} — Tarifa real: ${daysUsed}d usados a ${formatCurrency(priceUsed)}`;
        } else {
          // Fallback: auto-detection by item types
          refund = calcRefund(packTypes, itemDaysPaid, daysUsed, packUnitTotal);
          packLabel = `Pack · ${packItems.map(i => i.item_type).join(' + ')}`;
        }

        if (isFullyPaid) {
          groupBalance = -refund;
          groupReason = `${specificPack?.name || tag}: ${unusedDays}d antes (-${formatCurrency(refund)})`;
          itemBreakdowns.push({ label: packLabel, type: 'early', daysPaid: itemDaysPaid, daysUsed, daysOverdue: 0, amount: groupBalance });
        } else if (pendingAmountTotal > 0) {
          const ratio = priceOfAllPending > 0 ? packUnitTotal / priceOfAllPending : (packItems.length / allPendingItems.length);
          groupBalance = pendingAmountTotal * ratio;
          groupReason = `${specificPack?.name || tag}: saldo pendiente ${formatCurrency(groupBalance)}`;
          itemBreakdowns.push({ label: specificPack?.name || `Pack · ${packItems.map(i => i.item_type).join(' + ')}`, type: 'pending', daysPaid: itemDaysPaid, daysUsed, daysOverdue: 0, amount: groupBalance });
        }
      } else {
        const packId2 = packItems[0].pack_id;
        const specificPack2 = packId2 ? storePacks.find(p => p.id === packId2) : null;
        const onTimeLabel = specificPack2?.name || `Pack · ${packItems.map(i => i.item_type).join(' + ')}`;
        if (isFullyPaid) {
          groupBalance = 0;
          groupReason = `${specificPack2?.name || tag}: en fecha`;
          itemBreakdowns.push({ label: onTimeLabel, type: 'ontime', daysPaid: itemDaysPaid, daysUsed, daysOverdue: 0, amount: 0 });
        } else if (pendingAmountTotal > 0) {
          const ratio = priceOfAllPending > 0 ? packUnitTotal / priceOfAllPending : (packItems.length / allPendingItems.length);
          groupBalance = pendingAmountTotal * ratio;
          groupReason = `${specificPack2?.name || tag}: saldo pendiente ${formatCurrency(groupBalance)}`;
          itemBreakdowns.push({ label: onTimeLabel, type: 'pending', daysPaid: itemDaysPaid, daysUsed, daysOverdue: 0, amount: groupBalance });
        }
      }

      totalBalance += groupBalance;
      if (groupReason) reasons.push(groupReason);
    }

    // FASE 2: procesar ítems sueltos (sin pack)
    for (const item of toReturnItems) {
      if (processedBarcodes.has(item.barcode)) continue; // ya procesado en un pack

      const itemDaysPaid = item.item_days || rentalDays;
      const itemUnitPrice = item.unit_price || 0;
      const { daysOverdue, daysUsed: itemDaysUsed } = calcDaysInfo(itemDaysPaid);

      if (item === toReturnItems[0] && !processedPackInstances.size) {
        overallDaysUsed = itemDaysUsed;
        overallDaysPaid = itemDaysPaid;
      }

      let itemBalance = 0;
      let itemReason = "";
      const tag = item.barcode?.slice(-6) || '?';

      if (daysOverdue > 0) {
        itemBalance = calcSurcharge([item.item_type], itemDaysUsed, itemDaysPaid, daysOverdue, itemUnitPrice);
        itemReason = `${tag}: +${daysOverdue}d extra (${itemDaysPaid}d→${itemDaysUsed}d: +${formatCurrency(itemBalance)})`;
        itemBreakdowns.push({ label: `${item.internal_code || tag} · ${item.item_type}`, type: 'overdue', daysPaid: itemDaysPaid, daysUsed: itemDaysUsed, daysOverdue, amount: itemBalance });
      } else if (itemDaysUsed < itemDaysPaid) {
        const unusedDays = itemDaysPaid - itemDaysUsed;
        const refund = calcRefund([item.item_type], itemDaysPaid, itemDaysUsed, itemUnitPrice);
        if (isFullyPaid) {
          itemBalance = -refund;
          itemReason = `${tag}: ${unusedDays}d antes (tarifa ${itemDaysPaid}d→${itemDaysUsed}d: -${formatCurrency(refund)})`;
          itemBreakdowns.push({ label: `${item.internal_code || tag} · ${item.item_type}`, type: 'early', daysPaid: itemDaysPaid, daysUsed: itemDaysUsed, daysOverdue: 0, amount: itemBalance });
        } else if (pendingAmountTotal > 0) {
          const ratio = priceOfAllPending > 0 ? itemUnitPrice / priceOfAllPending : (1 / allPendingItems.length);
          itemBalance = pendingAmountTotal * ratio;
          itemReason = `${tag}: saldo pendiente ${formatCurrency(itemBalance)}`;
          itemBreakdowns.push({ label: `${item.internal_code || tag} · ${item.item_type}`, type: 'pending', daysPaid: itemDaysPaid, daysUsed: itemDaysUsed, daysOverdue: 0, amount: itemBalance });
        }
      } else {
        // EN FECHA
        if (isFullyPaid) {
          itemBalance = 0;
          itemReason = `${tag}: en fecha`;
          itemBreakdowns.push({ label: `${item.internal_code || tag} · ${item.item_type}`, type: 'ontime', daysPaid: itemDaysPaid, daysUsed: itemDaysUsed, daysOverdue: 0, amount: 0 });
        } else if (pendingAmountTotal > 0) {
          const ratio = priceOfAllPending > 0 ? itemUnitPrice / priceOfAllPending : (1 / allPendingItems.length);
          itemBalance = pendingAmountTotal * ratio;
          itemReason = `${tag}: saldo pendiente ${formatCurrency(itemBalance)}`;
          itemBreakdowns.push({ label: `${item.internal_code || tag} · ${item.item_type}`, type: 'pending', daysPaid: itemDaysPaid, daysUsed: itemDaysUsed, daysOverdue: 0, amount: itemBalance });
        }
      }

      totalBalance += itemBalance;
      if (itemReason) reasons.push(itemReason);
    }

    // Créditos y descuentos
    const pendingRefund = rental.pending_refund || 0;
    if (pendingRefund > 0) {
      const refund = pendingRefund * priceRatioDisplay;
      totalBalance -= refund;
      reasons.push(`Crédito previo: -${formatCurrency(refund)}`);
    }
    if (discountCredit > 0) {
      const disc = discountCredit * priceRatioDisplay;
      totalBalance -= disc;
      reasons.push(`Descuento ${discountDays}d: -${formatCurrency(disc)}`);
    }

    if (isFullyPendingRental) totalBalance = Math.max(0, totalBalance);

    const balanceReason = reasons.join(' | ') || 'Devolución en fecha';
    const pricePerDay = totalAmount > 0 ? totalAmount / rentalDays : 0;

    // Depósito
    const depositToReturn = rental.deposit || 0;
    let finalBalance = totalBalance;
    let depositRefund = depositToReturn;
    let balanceDetails = balanceReason;

    if (depositToReturn > 0) {
      if (totalBalance > 0 && depositToReturn >= totalBalance) {
        depositRefund = depositToReturn - totalBalance;
        finalBalance = -depositRefund;
        balanceDetails = `${balanceReason}. Deuda ${formatCurrency(totalBalance)} cubierta por depósito`;
      } else if (totalBalance > 0 && depositToReturn < totalBalance) {
        finalBalance = totalBalance - depositToReturn;
        depositRefund = 0;
        balanceDetails = `${balanceReason}. ${formatCurrency(depositToReturn)} del depósito aplicado`;
      } else if (totalBalance <= 0) {
        depositRefund = depositToReturn;
        finalBalance = totalBalance - depositToReturn;
        if (depositToReturn > 0) {
          balanceDetails = balanceReason ? `${balanceReason} + Depósito a devolver` : `Depósito a devolver`;
        }
      }
    }

    return {
      balance: Math.round(finalBalance * 100) / 100,
      daysUsed: overallDaysUsed,
      daysPaid: overallDaysPaid,
      pricePerDay: Math.round(pricePerDay * 100) / 100,
      serviceUsed: Math.round(priceOfReturned * 100) / 100,
      totalPaid: Math.round(actualPaidAmount * priceRatioDisplay * 100) / 100,
      itemsToReturn: toReturnItems.map(i => i.barcode),
      balanceReason: balanceDetails,
      originalPaymentMethod: rental.payment_method || "cash",
      depositOriginal: depositToReturn,
      depositRefund: Math.round(depositRefund * 100) / 100,
      pendingAmount: pendingAmountTotal,
      pendingRefund,
      rawBalance: Math.round(totalBalance * 100) / 100,
      isFullyPendingRental,
      itemBreakdowns
    };
  };
  // ============ PASO 1-4: PROCESAR DEVOLUCIÓN ============
  const processQuickReturn = async () => {
    
    // Verificar items seleccionados
    if (!toReturnItems || toReturnItems.length === 0) {
      toast.error('⚠️ Selecciona al menos un artículo para devolver');
      console.log('ERROR: No hay items seleccionados');
      return;
    }
    
    // Verificar ID del contrato
    if (!rental || !rental.id) {
      toast.error('⚠️ No hay contrato activo seleccionado');
      console.log('ERROR: No hay contrato activo');
      return;
    }
    
    try {
      // PASO 2: Cálculo financiero
      const settlement = calculateSettlement();
      
      if (!settlement) {
        toast.error("Error al calcular la liquidación");
        return;
      }
      
      console.log('Balance calculado:', settlement.balance);
      
      // CASO A: Saldo 0 y sin depósito - Proceder directamente (no se necesita caja)
      const hasDeposit = (rental.deposit || 0) > 0 && !rental.deposit_status;
      const allItemsReturning = settlement.itemsToReturn.length === rental.items.filter(i => !i.returned).length;
      const needsCashSession = Math.abs(settlement.balance) >= 0.01 || (hasDeposit && allItemsReturning);
      
      if (needsCashSession) {
        // Validar caja abierta ANTES de proceder
        const cashOk = await requireCashSession();
        if (!cashOk) return;
      }
      
      if (Math.abs(settlement.balance) < 0.01 && !needsCashSession) {
        console.log('CASO A: Saldo 0 sin depósito - Procesando directamente');
        await executeReturn(settlement.itemsToReturn);
        return;
      }
      
      if (Math.abs(settlement.balance) < 0.01 && needsCashSession) {
        // Saldo 0 pero hay depósito - proceder directo (backend valida sesión para depósito)
        console.log('CASO A+D: Saldo 0 con depósito - Procesando');
        await executeReturn(settlement.itemsToReturn);
        return;
      }
      
      // CASO B o C: Hay saldo pendiente - Mostrar modal de liquidación
      console.log('CASO B/C: Saldo pendiente - Mostrando modal');
      // Si el método original es 'pending' y hay que COBRAR al cliente,
      // inicializar a 'cash' para que el personal elija cómo cobrar (evitar bucle infinito)
      const initialPaymentMethod = 
        (settlement.originalPaymentMethod === 'pending' && settlement.balance > 0)
          ? 'cash'
          : settlement.originalPaymentMethod;
      setSettlementData({
        ...settlement,
        paymentMethod: initialPaymentMethod
      });
      setShowSettlementModal(true);
      
    } catch (error) {
      // PASO 4: Manejo de errores
      console.error("Error en processQuickReturn:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Error desconocido al procesar devolución";
      toast.error(`❌ Error: ${errorMsg}`);
    }
  };
  
  // PASO 3: Ejecutar la transacción en el backend
  const executeReturn = async (barcodes) => {
    console.log('=== EJECUTANDO DEVOLUCIÓN EN BACKEND ===');
    console.log('Barcodes a devolver:', barcodes);
    
    setProcessing(true);
    try {
      // Build quantities map for partial returns
      const quantitiesMap = {};
      barcodes.forEach(barcode => {
        const item = rental.items.find(i => i.barcode === barcode);
        if (item && isPartialReturnItem(item)) {
          quantitiesMap[barcode] = getReturnQuantity(item);
        }
      });
      
      console.log('Quantities map:', quantitiesMap);
      
      const response = await axios.post(`${API}/rentals/${rental.id}/return`, {
        barcodes: barcodes,
        quantities: quantitiesMap
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      console.log('Respuesta del backend:', response.data);
      
      // Calcular total de unidades devueltas
      const totalUnitsReturned = barcodes.reduce((sum, barcode) => {
        const item = rental.items.find(i => i.barcode === barcode);
        if (item && isPartialReturnItem(item)) {
          return sum + getReturnQuantity(item);
        }
        return sum + (item?.quantity || 1);
      }, 0);
      
      toast.success(`✅ Devolución procesada - ${totalUnitsReturned} unidad(es) devuelta(s)`);
      
      // ============ IMPRESIÓN DEL TICKET DE DEVOLUCIÓN (Non-blocking) ============
      // Mapear datos de artículos devueltos para el ticket
      const returnedItemsData = barcodes.map(barcode => {
        const item = rental.items.find(i => 
          (i.barcode && i.barcode.toUpperCase() === barcode.toUpperCase()) ||
          (i.internal_code && i.internal_code.toUpperCase() === barcode.toUpperCase()) ||
          i.item_id === barcode
        );
        return {
          name: item?.item_type || item?.brand || 'Artículo',
          internal_code: item?.internal_code || barcode,
          size: item?.size,
          days: item?.item_days || item?.days || rental.days || 1,
          price: item?.unit_price || 0,
          barcode: item?.barcode || barcode,
          quantity: isPartialReturnItem(item) ? getReturnQuantity(item) : 1,
          is_partial: isPartialReturnItem(item),
          startDate: rental.start_date || null,
          endDate: item?.end_date || rental.end_date || null
        };
      });
      
      // Calcular importe de liquidación (si hay)
      const refundAmount = settlementData.balance || 0;
      
      // Imprimir ticket si hay importe, depósito, o cualquier movimiento relevante
      const hasDepositMovement = (settlementData.depositOriginal || 0) > 0;
      if (refundAmount !== 0 || hasDepositMovement) {
        // Ruta unificada via iframe auto-print
        printTicket({
          ticketType: 'return',
          data: {
            operationNumber: response.data.operation_number,
            date: new Date().toISOString(),
            customer: rental.customer_name,
            dni: rental.customer_dni,
            returnedItems: returnedItemsData,
            refundAmount: refundAmount,
            paymentMethod: settlementData.paymentMethod || 'cash',
            depositOriginal: settlementData.depositOriginal || 0,
            depositRefund: settlementData.depositRefund || 0,
            depositAction: depositAction,
            balanceBreakdown: settlementData.balanceReason || ''
          }
        }).catch(err => {
          console.error('[Print] Error al imprimir ticket de devolución:', err);
        });
      } else {
        console.log('[Print] Importe 0 sin depósito — no se imprime ticket de devolución');
      }
      // ============ FIN IMPRESIÓN ============
      
      setShowSettlementModal(false);
      resetForm();
      loadPendingReturns();
      
      return response.data;
    } catch (error) {
      console.error("Error en executeReturn:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Error al procesar devolución";
      toast.error(`❌ ${errorMsg}`);
      throw error;
    } finally {
      setProcessing(false);
    }
  };
  
  // Confirmar liquidación con cobro/abono
  const confirmSettlement = async () => {
    console.log('=== CONFIRMANDO LIQUIDACIÓN ===');
    
    setSettlementProcessing(true);
    try {
      // Validar caja abierta como red de seguridad
      const cashOk = await requireCashSession();
      if (!cashOk) {
        setSettlementProcessing(false);
        return;
      }
      
      const { balance, itemsToReturn, paymentMethod, totalPaid } = settlementData;
      
      // CASO ESPECIAL: Anulación Total (Override activado)
      if (fullRefundOverride) {
        const refundAmount = totalPaid || 0;
        console.log('=== ANULACIÓN TOTAL - Devolviendo todo:', refundAmount);
        
        if (refundAmount > 0) {
          // Registrar salida de caja por el importe total
          await axios.post(`${API}/cash/movements`, {
            movement_type: "expense",
            category: "refund",
            amount: refundAmount,
            payment_method: paymentMethod || settlementData.originalPaymentMethod || 'cash',
            concept: `⚠️ ANULACIÓN TOTAL - ${rental.customer_name} (Devolución completa por excepción)`,
            reference_id: rental.id,
            notes: "Anulación total"
          }, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          toast.success(`⚠️ Anulación completada - Devuelto ${formatCurrency(refundAmount)} al cliente`);
        } else {
          toast.success(`⚠️ Anulación completada - Sin importe a devolver`);
        }
        
        // Ejecutar la devolución física
        await executeReturn(itemsToReturn);
        
        // Reset del override
        setFullRefundOverride(false);
        return;
      }
      
      // FLUJO NORMAL (sin override)
      if (balance > 0) {
        // CASO B: Cobrar al cliente — método LIBRE elegido por cajero
        const effectiveMethod = settlementMixedPayment?.method || paymentMethod || 'cash';
        const effectiveDetail = effectiveMethod === 'mixed'
          ? { cash: settlementMixedPayment?.cashAmount || 0, card: settlementMixedPayment?.cardAmount || 0 }
          : null;

        console.log('Registrando cobro:', balance, effectiveMethod, effectiveDetail);
        await axios.post(`${API}/rentals/${rental.id}/payment`, {
          amount: balance,
          payment_method: effectiveMethod,
          payment_detail: effectiveDetail
        }, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success(`💰 Cobro de ${formatCurrency(balance)} registrado`);
      } else if (balance < 0) {
        // CASO C: Devolver dinero al cliente
        const refundAmount = Math.abs(balance);
        console.log('Registrando reembolso:', refundAmount);
        await axios.post(`${API}/cash/movements`, {
          movement_type: "expense",
          category: "refund",
          amount: refundAmount,
          payment_method: paymentMethod,
          concept: `Devolución anticipada - ${rental.customer_name}`,
          reference_id: rental.id,
          notes: "Reembolso por devolución"
        }, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success(`💸 Reembolso de ${formatCurrency(refundAmount)} registrado`);
      }
      
      // Ahora ejecutar la devolución física
      await executeReturn(itemsToReturn);
      
    } catch (error) {
      console.error("Error en confirmSettlement:", error);
      toast.error(`❌ ${error.response?.data?.detail || "Error al procesar la liquidación"}`);
    } finally {
      setSettlementProcessing(false);
      setFullRefundOverride(false); // Reset always
    }
  };

  // ============ HORA DE GRACIA - LOAD & SAVE ============
  const loadGraceHour = async () => {
    try {
      const response = await axios.get(`${API}/settings`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data?.return_grace_hour) {
        setGraceHour(response.data.return_grace_hour);
      }
    } catch (error) {
      console.error("Error loading grace hour:", error);
    }
  };

  const saveGraceHour = async (newHour) => {
    setGraceHour(newHour);
    setSavingGraceHour(true);
    try {
      await axios.put(`${API}/settings/return-grace-hour`, 
        { return_grace_hour: newHour },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(`Hora de gracia actualizada: ${newHour}`);
    } catch (error) {
      toast.error("Error al guardar hora de gracia");
    } finally {
      setSavingGraceHour(false);
    }
  };

  useEffect(() => {
    if (barcodeRef.current) {
      barcodeRef.current.focus();
    }
    loadPendingReturns();
    loadGraceHour();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadPendingReturns();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // ============ AUTO-FOCUS FOR SCANNER IN CHANGE MODAL ============
  // When activeSwapIndex changes (user clicks "Sustituir" on an item), focus the input
  useEffect(() => {
    if (activeSwapIndex !== null && changeInputRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        changeInputRef.current?.focus();
        changeInputRef.current?.select();
      }, 50);
    }
  }, [activeSwapIndex]);

  const loadPendingReturns = async () => {
    try {
      const response = await axios.get(`${API}/rentals/pending/returns`);
      setPendingReturns(response.data);
    } catch (error) {
      console.error("Error loading pending returns:", error);
    }
  };

  // ============ HISTORIAL DE DEVOLUCIONES RECIENTES ============
  const loadRecentReturns = async () => {
    setLoadingRecentReturns(true);
    try {
      const response = await axios.get(`${API}/rentals/recent/completed`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setRecentReturns(response.data);
    } catch (error) {
      console.error("Error loading recent returns:", error);
    } finally {
      setLoadingRecentReturns(false);
    }
  };

  // Load recent returns on mount and after operations
  useEffect(() => {
    loadRecentReturns();
  }, []);

  const handleUndoReturn = async (rental) => {
    // If rental had cash movements, show confirmation dialog first
    if (rental.has_cash_movement && !showUndoConfirmDialog) {
      setPendingUndoRental(rental);
      setShowUndoConfirmDialog(true);
      return;
    }

    setUndoingReturn(rental.id);
    try {
      const response = await axios.post(
        `${API}/rentals/${rental.id}/undo-return`,
        { confirm_cash_adjustment: rental.has_cash_movement },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }}
      );
      
      toast.success(`✅ ${response.data.message}`);
      
      // Refresh both lists
      loadRecentReturns();
      loadPendingReturns();
      
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      
      if (errorDetail === "CASH_MOVEMENT_WARNING") {
        // This shouldn't happen with our flow, but handle it
        setPendingUndoRental(rental);
        setShowUndoConfirmDialog(true);
      } else if (typeof errorDetail === 'object' && errorDetail.code === "ITEMS_UNAVAILABLE") {
        // Show detailed error about unavailable items
        const itemsList = errorDetail.items.map(i => `• ${i.item_name || i.barcode}: ${i.reason}`).join('\n');
        toast.error(
          <div>
            <strong>⚠️ No se puede deshacer</strong>
            <p className="mt-1 text-sm">Artículos no disponibles:</p>
            <pre className="mt-1 text-xs whitespace-pre-wrap">{itemsList}</pre>
          </div>,
          { duration: 8000 }
        );
      } else {
        toast.error(`Error: ${errorDetail?.message || errorDetail || 'Error al deshacer devolución'}`);
      }
    } finally {
      setUndoingReturn(null);
    }
  };

  const confirmUndoWithCashWarning = async () => {
    if (!pendingUndoRental) return;
    
    setShowUndoConfirmDialog(false);
    // Call with cash confirmation
    const rentalWithConfirm = { ...pendingUndoRental, has_cash_movement: true };
    await handleUndoReturn(rentalWithConfirm);
    setPendingUndoRental(null);
  };

  const cancelUndoConfirm = () => {
    setShowUndoConfirmDialog(false);
    setPendingUndoRental(null);
  };

  // Format time for recent returns
  const formatReturnTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const formatReturnDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    
    if (isToday) return 'Hoy';
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
  };

  const handleBarcodeScan = async (e) => {
    if (e.key !== 'Enter' || !barcodeInput.trim()) return;
    
    const barcode = barcodeInput.trim();
    setBarcodeInput("");
    
    // If no rental loaded, find rental by barcode
    if (!rental) {
      setLoading(true);
      try {
        const response = await rentalApi.getByBarcode(barcode);
        setRental(response.data);
        toast.success(`Alquiler encontrado: ${response.data.customer_name}`);
        
        // DATE VALIDATION on first scan (inline: rental state not yet updated, use response.data)
        const rentalData = response.data;
        const codeUpper = barcode.toUpperCase();
        const matchedItem = (rentalData.items || []).find(i =>
          (i.barcode && i.barcode.toUpperCase() === codeUpper) ||
          (i.internal_code && i.internal_code.toUpperCase() === codeUpper) ||
          (i.item_id && i.item_id === barcode)
        );
        
        if (matchedItem && rentalData.end_date) {
          const expectedDate = new Date(rentalData.end_date);
          expectedDate.setHours(0, 0, 0, 0);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          if (today.getTime() === expectedDate.getTime()) {
            // Date matches - silent add
            setScannedBarcodes([barcode]);
          } else {
            // Date mismatch - show warning (don't auto-mark the item)
            setDateWarningData({
              itemName: matchedItem.item_type || matchedItem.brand || 'artículo',
              expectedDate,
              isEarly: today < expectedDate,
              addAction: () => setScannedBarcodes([barcode])
            });
            setDateWarningModal(true);
          }
        } else {
          // No matching item found or no end_date - auto-mark silently
          setScannedBarcodes([barcode]);
        }
      } catch (error) {
        toast.error("No se encontró alquiler activo para este artículo");
      } finally {
        setLoading(false);
        refocusBarcodeInput(); // Re-focus for next scan
      }
      return;
    }
    
    // Rental loaded, add barcode to scanned list
    // Search by barcode, internal_code, or item_id (case-insensitive)
    const codeUpper = barcode.toUpperCase();
    const item = rental.items.find(i => 
      (i.barcode && i.barcode.toUpperCase() === codeUpper) ||
      (i.internal_code && i.internal_code.toUpperCase() === codeUpper) ||
      (i.item_id && i.item_id === barcode)
    );
    if (!item) {
      toast.error("Este artículo no pertenece a este alquiler");
      refocusBarcodeInput();
      return;
    }
    
    if (item.returned) {
      toast.info("Este artículo ya fue devuelto");
      refocusBarcodeInput();
      return;
    }
    
    // Check if already scanned (using the item's primary identifier) - TOGGLE LOGIC
    const itemBarcode = item.barcode || barcode;
    const isAlreadyScanned = scannedBarcodes.some(code => 
      code.toUpperCase() === (item.barcode || "").toUpperCase() ||
      code.toUpperCase() === (item.internal_code || "").toUpperCase() ||
      code === item.item_id
    );
    
    if (isAlreadyScanned) {
      // TOGGLE OFF: Remove from scanned list
      setScannedBarcodes(prev => prev.filter(code => 
        code.toUpperCase() !== (item.barcode || "").toUpperCase() &&
        code.toUpperCase() !== (item.internal_code || "").toUpperCase() &&
        code !== item.item_id
      ));
      toast.info(`${item.item_type || item.brand} desmarcado`);
      refocusBarcodeInput();
      return;
    }
    
    // TOGGLE ON: Store the item's barcode for processing (backend expects barcode)
    // DATE VALIDATION: check if today matches the expected return date
    const addFn = () => setScannedBarcodes(prev => [...prev, itemBarcode]);
    checkDateAndAdd(item, itemBarcode, `${item.item_type || item.brand} marcado ✓`, addFn);
    refocusBarcodeInput(); // Re-focus for next scan
  };

  // HELPER: Re-focus barcode input with text selection (for barcode scanner optimization)
  const refocusBarcodeInput = () => {
    setTimeout(() => {
      if (barcodeRef.current) {
        barcodeRef.current.focus();
        barcodeRef.current.select(); // Select all text so next scan overwrites
      }
    }, 50);
  };

  const processReturn = async () => {
    if (!rental || scannedBarcodes.length === 0) return;
    
    // Verificar si se van a devolver TODOS los artículos
    const allItemsScanned = rental.items.every(item => 
      item.returned || scannedBarcodes.includes(item.barcode)
    );
    
    // Si hay depósito y se devuelven todos los artículos, mostrar diálogo
    if (allItemsScanned && rental.deposit > 0 && !rental.deposit_status) {
      setShowDepositDialog(true);
      return;
    }
    
    // Procesar devolución normal (sin depósito o ya procesado)
    await confirmDepositAndReturn();
  };
  
  const confirmDepositAndReturn = async () => {
    setProcessing(true);
    try {
      const response = await rentalApi.processReturn(
        rental.id, 
        scannedBarcodes,
        depositAction,
        forfeitReason
      );
      
      if (response.data.status === 'returned') {
        // Mostrar mensaje según acción de depósito
        if (response.data.deposit_returned) {
          toast.success(`✅ Devolución completada. Depósito devuelto: ${formatCurrency(response.data.deposit_amount)}`);
        } else if (response.data.deposit_forfeited) {
          toast.success(`✅ Devolución completada. Depósito incautado: ${formatCurrency(response.data.deposit_amount)}`);
        } else {
          toast.success("✅ Devolución completada");
        }
        resetForm();
        loadPendingReturns();
      } else {
        toast.warning(`Devolución parcial: ${response.data.pending_items.length} artículos pendientes`);
        const updatedRental = await rentalApi.getById(rental.id);
        setRental(updatedRental.data);
        setScannedBarcodes([]);
        loadPendingReturns();
      }
      setShowDepositDialog(false);
    } catch (error) {
      console.error("[Returns] Error en devolución:", error);
      console.error("[Returns] Error response:", error.response);
      
      // Extraer mensaje de error correctamente
      let errorMessage = "Error al procesar devolución";
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const resetForm = () => {
    setRental(null);
    setScannedBarcodes([]);
    setBarcodeInput("");
    refocusBarcodeInput(); // Use helper with select
  };

  const loadRentalById = async (rentalId) => {
    try {
      const response = await rentalApi.getById(rentalId);
      setRental(response.data);
      toast.success("Alquiler cargado");
    } catch (error) {
      toast.error("Error al cargar alquiler");
    }
  };

  const contactCustomer = (phone) => {
    if (!phone) {
      toast.error("No hay teléfono registrado para este cliente");
      return;
    }
    // Clean phone number and open WhatsApp
    const cleanPhone = phone.replace(/\D/g, '');
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
  };

  // Open customer info modal
  const openCustomerModal = async (rentalData) => {
    setCustomerLoading(true);
    setShowCustomerModal(true);
    
    try {
      // Get full customer data if we have customer_id
      let customerData = {
        name: rentalData.customer_name,
        dni: rentalData.customer_dni,
        phone: rentalData.customer_phone,
        email: rentalData.customer_email,
        hotel: rentalData.customer_hotel || rentalData.hotel,
        notes: rentalData.customer_notes,
        pending_items: rentalData.pending_items || [],
        rental_id: rentalData.id,
        end_date: rentalData.end_date,
        days_overdue: rentalData.days_overdue || 0
      };
      
      // Try to get more customer details from API
      if (rentalData.customer_id) {
        try {
          const response = await axios.get(`${API}/customers/${rentalData.customer_id}`);
          customerData = {
            ...customerData,
            ...response.data,
            pending_items: rentalData.pending_items || [],
            rental_id: rentalData.id,
            end_date: rentalData.end_date,
            days_overdue: rentalData.days_overdue || 0
          };
        } catch (e) {
          // Continue with rental data if customer fetch fails
        }
      }
      
      setSelectedCustomer(customerData);
    } catch (error) {
      toast.error("Error al cargar datos del cliente");
    } finally {
      setCustomerLoading(false);
    }
  };

  // Send WhatsApp with predefined message
  const sendWhatsAppMessage = (phone, customerName) => {
    if (!phone) {
      toast.error("No hay teléfono registrado para este cliente");
      return;
    }
    const cleanPhone = phone.replace(/\D/g, '');
    const message = encodeURIComponent(
      `Hola ${customerName}, te contactamos de la tienda de esquí por la devolución del material. ¿Cuándo podrías pasarte a devolverlo? Gracias.`
    );
    window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
  };

  // Call phone directly
  const callPhone = (phone) => {
    if (!phone) {
      toast.error("No hay teléfono registrado");
      return;
    }
    window.open(`tel:${phone}`, '_self');
  };

  // Send email
  const sendEmail = (email, customerName) => {
    if (!email) {
      toast.error("No hay email registrado");
      return;
    }
    const subject = encodeURIComponent("Recordatorio de devolución de material - Tienda de Esquí");
    const body = encodeURIComponent(
      `Hola ${customerName},\n\nTe contactamos desde la tienda de esquí para recordarte la devolución del material alquilado.\n\nPor favor, contacta con nosotros para coordinar la devolución.\n\nGracias.`
    );
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
  };

  // ── CORRECT refund calculation ────────────────────────────────────────────
  // Formula: Refund = paid_amount - adjusted_cost_for_days_used
  // adjusted_cost preserves the original discount ratio (tariff_used / tariff_full × paid)
  // Hard cap: result can NEVER exceed paid_amount
  const calculateRefundAmount = (daysToRefund) => {
    if (!rental || daysToRefund <= 0) return 0;
    const paidAmount   = rental.paid_amount || rental.total_amount || 0;
    const originalDays = rental.days || 1;
    const daysUsed     = Math.max(0, originalDays - daysToRefund);

    const allItemTypes = (rental.items || []).filter(i => !i.returned).map(i => i.item_type);

    const tariffFull = calculateTariffPrice(allItemTypes, originalDays, storePacks, storeTariffs);
    const tariffUsed = daysUsed > 0
      ? calculateTariffPrice(allItemTypes, daysUsed, storePacks, storeTariffs)
      : 0;

    let refund;
    if (tariffFull > 0) {
      const discountRatio = paidAmount / tariffFull;
      const adjustedCost  = tariffUsed * discountRatio;
      refund = paidAmount - adjustedCost;
    } else {
      // Fallback: proportional (no tariff data available)
      refund = paidAmount * (daysToRefund / originalDays);
    }

    // HARD CAP — impossible to return more than what was paid
    return parseFloat(Math.min(Math.max(0, refund), paidAmount).toFixed(2));
  };

  // Helper: compute discount-days credit anchored to actual paid amount
  const computeDiscountCredit = (newDiscountDays, rentalObj) => {
    if (!rentalObj || newDiscountDays <= 0) return 0;
    const paidAmount   = rentalObj.paid_amount || rentalObj.total_amount || 0;
    const originalDays = rentalObj.days || 1;
    const daysUsed     = Math.max(0, originalDays - newDiscountDays);

    const itemTypes = (rentalObj.items || []).filter(i => !i.returned).map(i => i.item_type);
    const tariffFull = calculateTariffPrice(itemTypes, originalDays, storePacks, storeTariffs);
    const tariffUsed = daysUsed > 0
      ? calculateTariffPrice(itemTypes, daysUsed, storePacks, storeTariffs)
      : 0;

    let credit;
    if (tariffFull > 0) {
      const discountRatio = paidAmount / tariffFull;
      credit = paidAmount - (tariffUsed * discountRatio);
    } else {
      credit = paidAmount * (newDiscountDays / originalDays);
    }

    // HARD CAP
    return parseFloat(Math.min(Math.max(0, credit), paidAmount).toFixed(2));
  };

  // Open refund dialog
  const openRefundDialog = () => {
    if (!rental) return;
    
    // Calculate remaining days that could be refunded
    const today = new Date();
    const endDate = new Date(rental.end_date);
    const startDate = new Date(rental.start_date);
    
    // Calculate days used
    const msPerDay = 24 * 60 * 60 * 1000;
    const daysElapsed = Math.max(1, Math.ceil((today - startDate) / msPerDay));
    const maxRefundDays = Math.max(0, rental.days - daysElapsed);
    
    if (maxRefundDays === 0) {
      toast.error("No hay días disponibles para reembolsar");
      return;
    }
    
    setRefundDays(1);
    setRefundAmount(calculateRefundAmount(1));
    setRefundReason("");
    // SECURITY: Pre-select original payment method (forced, not editable)
    setRefundMethod(rental.payment_method || "cash");
    setShowRefundDialog(true);
  };

  // Handle refund days change
  const handleRefundDaysChange = (days) => {
    const numDays = parseInt(days) || 1;
    setRefundDays(numDays);
    setRefundAmount(calculateRefundAmount(numDays));
  };

  // Process the refund
  const processRefund = async () => {
    if (!rental || refundDays <= 0 || refundAmount <= 0) {
      toast.error("Verifica los datos del reembolso");
      return;
    }
    
    setProcessingRefund(true);
    try {
      const response = await axios.post(`${API}/rentals/${rental.id}/refund`, {
        days_to_refund: refundDays,
        refund_amount: refundAmount,
        payment_method: refundMethod,
        reason: refundReason
      });
      
      toast.success(`Reembolso de ${formatCurrency(refundAmount)} procesado correctamente`);
      setShowRefundDialog(false);
      
      // Reload the rental data
      const updatedRental = await rentalApi.getById(rental.id);
      setRental(updatedRental.data);
      loadPendingReturns();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al procesar reembolso");
    } finally {
      setProcessingRefund(false);
    }
  };

  // Calculate max refundable days
  const getMaxRefundDays = () => {
    if (!rental) return 0;
    const today = new Date();
    const startDate = new Date(rental.start_date);
    const msPerDay = 24 * 60 * 60 * 1000;
    const daysElapsed = Math.max(1, Math.ceil((today - startDate) / msPerDay));
    return Math.max(0, rental.days - daysElapsed);
  };

  const pendingItems = rental?.items.filter(i => !i.returned && !scannedBarcodes.includes(i.barcode)) || [];
  const returnedItems = rental?.items.filter(i => i.returned) || [];
  const toReturnItems = rental?.items.filter(i => !i.returned && scannedBarcodes.includes(i.barcode)) || [];
  const maxRefundDays = getMaxRefundDays();
  
  // ============ HELPER: CALCULAR CANTIDAD TOTAL DE UNIDADES (NO LÍNEAS) ============
  const getTotalUnits = (items) => {
    return items.reduce((total, item) => {
      if (isPartialReturnItem(item)) {
        // Para items genéricos con devolución parcial, usar la cantidad seleccionada
        return total + getReturnQuantity(item);
      }
      // Para items regulares, contar como 1 unidad
      return total + (item.quantity || 1);
    }, 0);
  };
  
  const getTotalUnitsSimple = (items) => {
    // Versión simple que suma todas las cantidades sin considerar selección parcial
    return items.reduce((total, item) => total + (item.quantity || 1), 0);
  };
  
  // Calcular totales de UNIDADES (no líneas)
  const pendingUnitsCount = getTotalUnitsSimple(pendingItems);
  const toReturnUnitsCount = getTotalUnits(toReturnItems);
  const totalPendingUnits = getTotalUnitsSimple(rental?.items.filter(i => !i.returned) || []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
  };

  // Marcar TODOS los items pendientes como listos para devolver
  const markAllForReturn = () => {
    if (!rental) return;
    const allPendingBarcodes = rental.items
      .filter(i => !i.returned)
      .map(i => i.barcode);
    setScannedBarcodes(allPendingBarcodes);
    const totalUnits = rental.items
      .filter(i => !i.returned)
      .reduce((sum, i) => sum + (i.quantity || 1), 0);
    toast.success(`${totalUnits} unidades marcadas para devolver`);
  };

  // Calcular si todos están listos
  const allItemsReady = rental && toReturnItems.length > 0 && pendingItems.length === 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="returns-page">
      
      {/* ============================================== */}
      {/* ZONA SUPERIOR: MOSTRADOR DE RECEPCIÓN         */}
      {/* ============================================== */}
      <div className="bg-white border-b-2 border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          
          {/* TÍTULO Y CAMPO DE BÚSQUEDA CENTRAL */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-black text-slate-900 mb-2 flex items-center justify-center gap-3" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <ArrowDownToLine className="h-8 w-8 text-emerald-600" />
              Mostrador de Recepción
            </h1>
            <p className="text-slate-500">Escanea cualquier código de barras para cargar el contrato</p>
          </div>
          
          {/* CAMPO DE ESCANEO GRANDE Y CENTRADO */}
          <div className="max-w-2xl mx-auto mb-6">
            <div className="relative">
              <Scan className={`absolute left-5 top-1/2 -translate-y-1/2 h-7 w-7 ${globalScannerActive ? 'text-emerald-600 animate-pulse' : 'text-emerald-500'}`} />
              <Input
                ref={barcodeRef}
                placeholder="🔫 Escanear código de barras..."
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                onKeyDown={handleBarcodeScan}
                className={`h-16 pl-14 pr-14 text-xl font-mono text-center bg-emerald-500/10 border-2 focus:ring-emerald-200 rounded-2xl shadow-inner transition-all ${
                  globalScannerActive 
                    ? 'border-emerald-500 ring-2 ring-emerald-300 bg-emerald-500/20' 
                    : 'border-emerald-200 focus:border-emerald-500'
                }`}
                data-testid="return-barcode-input"
                autoFocus
              />
              {loading && (
                <Loader2 className="absolute right-5 top-1/2 -translate-y-1/2 h-6 w-6 animate-spin text-emerald-600" />
              )}
              {globalScannerActive && !loading && (
                <Radio className="absolute right-5 top-1/2 -translate-y-1/2 h-6 w-6 text-emerald-600 animate-pulse" />
              )}
            </div>
            <p className={`text-center text-sm mt-2 transition-colors ${globalScannerActive ? 'text-emerald-600 font-medium' : 'text-slate-600'}`}>
              {globalScannerActive 
                ? '📡 Escáner detectado - Listo para recibir códigos' 
                : 'Escanea o escribe el código y presiona Enter'}
            </p>
          </div>

          {/* ======= ÁREA DE CONTRATO ACTIVO ======= */}
          {rental && (
            <div className="bg-slate-50 rounded-2xl p-6 border-2 border-slate-200 shadow-inner">
              <div className="grid grid-cols-12 gap-6">
                
                {/* COLUMNA IZQUIERDA: FICHA DEL CLIENTE */}
                <div className="col-span-4">
                  <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm h-full">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-16 w-16 border-2 border-emerald-200">
                        <AvatarFallback className="bg-emerald-500/20 text-emerald-700 text-xl font-bold">
                          {rental.customer_name?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <h3 
                          onClick={() => openCustomerModal(rental)}
                          className="font-bold text-lg text-emerald-700 hover:text-emerald-900 cursor-pointer hover:underline transition-colors"
                          title="Ver ficha completa del cliente"
                        >
                          {rental.customer_name}
                        </h3>
                        <p className="text-sm text-slate-500 font-mono">{rental.customer_dni}</p>
                        <Badge variant="outline" className="mt-2 bg-emerald-500/10 text-emerald-700 border-emerald-200">
                          Contrato #{rental.id?.substring(0, 8)}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Fechas del Contrato */}
                    <div className="mt-4 p-3 rounded-lg bg-blue-500/10 border border-blue-100">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-blue-500" />
                          <span className="text-slate-600">Inicio:</span>
                          <span className="font-semibold text-slate-800">
                            {rental.start_date ? new Date(rental.start_date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-600">Fin:</span>
                          <span className="font-semibold text-slate-800">
                            {rental.end_date ? new Date(rental.end_date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Info Rápida del Contrato */}
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div className="p-3 rounded-lg bg-slate-50 text-center">
                        <Clock className="h-4 w-4 mx-auto text-slate-600 mb-1" />
                        <p className="text-xs text-slate-500">Período</p>
                        <p className="font-bold text-slate-800">{rental.days} días</p>
                      </div>
                      <div className="p-3 rounded-lg bg-slate-50 text-center">
                        <DollarSign className="h-4 w-4 mx-auto text-slate-600 mb-1" />
                        <p className="text-xs text-slate-500">Total</p>
                        <p className="font-bold text-slate-800">{formatCurrency(rental.total_amount)}</p>
                      </div>
                    </div>
                    
                    {/* Días a descontar (No facturables) */}
                    <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-amber-800">Días a descontar</p>
                          <p className="text-xs text-amber-600">Cierre estación, enfermedad, etc.</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => {
                              const newDiscount = Math.max(0, discountDays - 1);
                              setDiscountDays(newDiscount);
                              setDiscountCredit(computeDiscountCredit(newDiscount, rental));
                            }}
                            disabled={discountDays <= 0}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-8 text-center font-bold text-lg">{discountDays}</span>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => {
                              const maxDays = rental?.days || 1;
                              const newDiscount = Math.min(maxDays, discountDays + 1);
                              setDiscountDays(newDiscount);
                              setDiscountCredit(computeDiscountCredit(newDiscount, rental));
                            }}
                            disabled={discountDays >= (rental?.days || 1)}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      {discountDays > 0 && (() => {
                        const isPending = rental.payment_method === 'pending' || (rental.pending_amount || 0) > 0;
                        const currentPending = rental.pending_amount || 0;
                        const newPending = isPending ? Math.max(0, currentPending - discountCredit) : 0;
                        return (
                          <div className="mt-2 pt-2 border-t border-amber-200 space-y-1.5">
                            <div className="flex justify-between text-xs text-amber-800">
                              <span>{rental.days}d → {rental.days - discountDays}d facturables</span>
                              <span className="font-bold">-{formatCurrency(discountCredit)}</span>
                            </div>
                            {isPending ? (
                              <div className="bg-amber-50 border border-amber-200/60 rounded-md px-3 py-2">
                                <div className="flex justify-between text-xs text-amber-900">
                                  <span>Pendiente actual:</span>
                                  <span className="line-through">{formatCurrency(currentPending)}</span>
                                </div>
                                <div className="flex justify-between text-sm font-bold text-amber-900 mt-0.5">
                                  <span>Nuevo pendiente:</span>
                                  <span>{formatCurrency(newPending)}</span>
                                </div>
                              </div>
                            ) : (
                              <p className="text-xs text-amber-600">
                                Se abonará al cliente en la liquidación.
                              </p>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    
                    {/* Alerta de pago pendiente */}
                    {rental.pending_amount > 0 && (
                      <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-red-500" />
                        <span className="text-sm font-medium text-red-700">
                          Pendiente: {formatCurrency(rental.pending_amount)}
                        </span>
                      </div>
                    )}
                    {/* Alerta de crédito pendiente por días descontados */}
                    {rental.pending_refund > 0 && (
                      <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-2">
                        <Clock className="h-5 w-5 text-amber-500" />
                        <span className="text-sm font-medium text-amber-700">
                          Crédito pendiente: -{formatCurrency(rental.pending_refund)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* COLUMNA DERECHA: LISTADO DE ARTÍCULOS */}
                <div className="col-span-8">
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                    
                    {/* Header con contador */}
                    <div className="bg-slate-800 text-white px-5 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Package className="h-5 w-5" />
                        <span className="font-bold">Artículos del Contrato</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className="bg-emerald-500">
                          {toReturnUnitsCount} unidades listas
                        </Badge>
                        <Badge className="bg-slate-600">
                          {pendingUnitsCount} unidades pendientes
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Encabezado de Columnas */}
                    <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <div className="w-10 flex-shrink-0"></div>
                      <div className="flex-1">Producto</div>
                      <div className="w-32 text-center flex-shrink-0">Código</div>
                      <div className="w-32 text-center flex-shrink-0">Talla/Cantidad</div>
                      <div className="w-28 text-right flex-shrink-0">Estado</div>
                    </div>
                    
                    {/* Lista Vertical de Artículos (Row Layout) */}
                    <div className="divide-y divide-slate-100 max-h-[280px] overflow-y-auto">
                      {rental.items.filter(i => !i.returned).map((item, idx) => {
                        const isScanned = scannedBarcodes.includes(item.barcode);
                        const isPartialItem = isPartialReturnItem(item);
                        const pendingQty = getPendingQuantity(item);
                        const returnQty = getReturnQuantity(item);
                        
                        // Toggle handler - marcar/desmarcar
                        const handleToggle = () => {
                          if (!isPartialItem) {
                            // Regular item: simple toggle
                            if (isScanned) {
                              setScannedBarcodes(prev => prev.filter(code => code !== item.barcode));
                              toast.info(`${item.item_type || item.brand} desmarcado`);
                            } else {
                              // DATE VALIDATION: check if today matches expected return date
                              const addFn = () => setScannedBarcodes(prev => [...prev, item.barcode]);
                              checkDateAndAdd(item, item.barcode, `${item.item_type || item.brand} marcado`, addFn);
                            }
                          }
                          // For partial items, clicking opens quantity controls (handled by UI)
                        };
                        
                        return (
                          <div 
                            key={item.barcode || idx}
                            onClick={handleToggle}
                            className={`px-4 py-3 ${!isPartialItem ? 'cursor-pointer' : ''} transition-all duration-200 flex items-center gap-4 ${
                              isScanned 
                                ? 'bg-emerald-500/10 border-l-4 border-l-emerald-500' 
                                : 'bg-white hover:bg-slate-50 border-l-4 border-l-transparent'
                            }`}
                          >
                            {/* Icono de Estado */}
                            <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                              isScanned 
                                ? 'bg-emerald-500 text-white' 
                                : 'bg-slate-100 text-slate-600'
                            }`}>
                              {isScanned ? <Check className="h-5 w-5" /> : <Package className="h-5 w-5" />}
                            </div>
                            
                            {/* Nombre del Producto */}
                            <div className="flex-1 min-w-0">
                              <p className={`font-semibold truncate ${isScanned ? 'text-emerald-800' : 'text-slate-800'}`}>
                                {isPartialItem && (
                                  <span className="font-bold text-blue-600 mr-1">
                                    {item.quantity}x
                                  </span>
                                )}
                                {item.item_type || 'Artículo'}
                                {item.brand && <span className="font-normal text-slate-500 ml-1">- {item.brand} {item.model}</span>}
                              </p>
                              {isPartialItem && (
                                <p className="text-xs text-slate-500 mt-1">
                                  {item.returned_quantity || 0} devuelto(s) / {item.quantity} total
                                </p>
                              )}
                            </div>
                            
                            {/* Código */}
                            <div className="flex-shrink-0 w-32 text-center">
                              <span className={`font-mono text-sm px-2 py-1 rounded ${
                                isScanned ? 'bg-emerald-500/20 text-emerald-700' : 'bg-slate-100 text-slate-600'
                              }`}>
                                {item.internal_code || item.barcode?.substring(0, 12) || '-'}
                              </span>
                            </div>
                            
                            {/* Talla / Cantidad Selector */}
                            <div className="flex-shrink-0 w-32 text-center">
                              {isPartialItem ? (
                                <div className="flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 w-7 p-0"
                                    onClick={() => {
                                      const newQty = Math.max(0, returnQty - 1);
                                      setReturnQuantity(item.barcode, newQty, pendingQty);
                                    }}
                                  >
                                    -
                                  </Button>
                                  <Input
                                    type="number"
                                    min="0"
                                    max={pendingQty}
                                    value={returnQty}
                                    onChange={(e) => {
                                      const val = parseInt(e.target.value) || 0;
                                      setReturnQuantity(item.barcode, val, pendingQty);
                                    }}
                                    className="h-7 w-14 text-center font-bold"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 w-7 p-0"
                                    onClick={() => {
                                      const newQty = Math.min(pendingQty, returnQty + 1);
                                      setReturnQuantity(item.barcode, newQty, pendingQty);
                                    }}
                                  >
                                    +
                                  </Button>
                                </div>
                              ) : item.size ? (
                                <Badge variant="outline" className={isScanned ? 'border-emerald-300 text-emerald-700' : ''}>
                                  T. {item.size}
                                </Badge>
                              ) : (
                                <span className="text-xs text-slate-600">-</span>
                              )}
                            </div>
                            
                            {/* Estado */}
                            <div className="flex-shrink-0 w-28 text-right">
                              {isScanned ? (
                                <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-600">
                                  <CheckCircle className="h-4 w-4" />
                                  {isPartialItem ? `${returnQty}/${pendingQty}` : 'LISTO'}
                                </span>
                              ) : (
                                <span className="text-sm text-slate-600">
                                  {isPartialItem ? `0/${pendingQty}` : 'Pendiente'}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Already returned items (dimmed) */}
                      {rental.items.filter(i => i.returned).length > 0 && (
                        <>
                          <div className="px-4 py-2 bg-slate-100 border-t border-slate-200">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                              Ya devueltos ({rental.items.filter(i => i.returned).length})
                            </p>
                          </div>
                          {rental.items.filter(i => i.returned).map((item, idx) => (
                            <div 
                              key={`returned-${item.barcode || idx}`}
                              className="px-4 py-2 bg-slate-50 flex items-center gap-4 opacity-50 border-l-4 border-l-slate-300"
                              data-testid={`returned-item-${item.barcode}`}
                            >
                              <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-slate-200 text-slate-500">
                                <CheckCircle className="h-5 w-5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-slate-500 truncate">
                                  {item.item_type || 'Artículo'}
                                  {item.brand && <span className="font-normal ml-1">- {item.brand}</span>}
                                </p>
                              </div>
                              <div className="flex-shrink-0 w-32 text-center">
                                <span className="font-mono text-sm text-slate-600">
                                  {item.internal_code || item.barcode?.substring(0, 12)}
                                </span>
                              </div>
                              <div className="flex-shrink-0 w-32 text-center">
                                {item.size && <Badge variant="outline" className="text-slate-600">T. {item.size}</Badge>}
                              </div>
                              <div className="flex-shrink-0 w-28 text-right">
                                <span className="text-sm font-semibold text-slate-600">DEVUELTO</span>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* ======= BOTONERA DE ACCIÓN RÁPIDA ======= */}
              <div className="flex items-center justify-center gap-4 mt-6 pt-6 border-t border-slate-300">
                {/* Devolución Masiva */}
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={markAllForReturn}
                  className="h-14 px-6 border-2 border-blue-300 text-blue-700 hover:bg-blue-500/10 font-semibold"
                  disabled={pendingItems.length === 0}
                >
                  <CheckCheck className="h-5 w-5 mr-2" />
                  Marcar TODO ({totalPendingUnits} unidades)
                </Button>
                
                {/* Cambio/Sustitución */}
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={() => openChangeModal(rental)}
                  className="h-14 px-6 border-2 border-orange-300 text-orange-700 hover:bg-orange-50 font-semibold"
                >
                  <ArrowLeftRight className="h-5 w-5 mr-2" />
                  Cambio/Sustitución
                </Button>
                
                {/* Procesar Devolución */}
                <Button 
                  size="lg"
                  onClick={processQuickReturn}
                  disabled={toReturnItems.length === 0 || processing}
                  className={`h-14 px-8 font-bold text-lg ${
                    allItemsReady 
                      ? 'bg-emerald-600 hover:bg-emerald-700 animate-pulse' 
                      : 'bg-emerald-600 hover:bg-emerald-700'
                  }`}
                >
                  {processing ? (
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  ) : (
                    <PackageCheck className="h-5 w-5 mr-2" />
                  )}
                  PROCESAR DEVOLUCIÓN ({toReturnUnitsCount} unidades)
                </Button>
                
                {/* Cancelar */}
                <Button 
                  variant="ghost" 
                  size="lg"
                  onClick={resetForm}
                  className="h-14 px-6 text-slate-500 hover:text-white hover:bg-slate-600"
                >
                  <X className="h-5 w-5 mr-2" />
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ============================================== */}
      {/* ZONA INFERIOR: COLAS DE TRABAJO               */}
      {/* ============================================== */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* TABLA 1: PENDIENTES DE HOY (Prioridad Alta) */}
          <Card className="border-2 border-red-200 shadow-lg overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-red-500 to-red-600 text-white py-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-bold flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  PENDIENTES DE HOY
                </CardTitle>
                <Badge className="bg-white text-red-600 font-bold text-lg px-3">
                  {pendingReturns.today?.length || 0}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {pendingReturns.today?.length > 0 ? (
                <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
                  {pendingReturns.today.map((r, idx) => (
                    <div 
                      key={r.id || idx}
                      onClick={() => quickReturn(r.id, r.customer_name)}
                      className="p-4 hover:bg-red-50 cursor-pointer transition-colors flex items-center justify-between group"
                    >
                      <div className="flex items-center gap-4">
                        <Avatar className="h-12 w-12 border-2 border-red-200">
                          <AvatarFallback className="bg-red-100 text-red-700 font-bold">
                            {r.customer_name?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-bold text-slate-900">{r.customer_name}</p>
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <Package className="h-3 w-3" />
                            <span>{r.pending_items?.reduce((sum, i) => sum + (i.quantity || 1), 0) || r.items?.filter(i => !i.returned).reduce((sum, i) => sum + (i.quantity || 1), 0) || '?'} unidades</span>
                            <span className="text-slate-300">•</span>
                            <span>{formatCurrency(r.total_amount)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                          HOY
                        </Badge>
                        <ArrowRight className="h-5 w-5 text-slate-300 group-hover:text-red-500 transition-colors" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-slate-600">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 text-emerald-300" />
                  <p className="font-medium">¡Sin devoluciones pendientes hoy!</p>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* TABLA 2: RESTO DE DEVOLUCIONES (Futuras y Atrasadas) */}
          <Card className="border-2 border-slate-200 shadow-lg overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-slate-700 to-slate-800 text-white py-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-bold flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  OTRAS DEVOLUCIONES
                </CardTitle>
                <Badge className="bg-white text-slate-700 font-bold text-lg px-3">
                  {pendingReturns.other_days?.length || 0}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {pendingReturns.other_days?.length > 0 ? (
                <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
                  {pendingReturns.other_days.map((r, idx) => {
                    const endDate = new Date(r.end_date);
                    const today = new Date();
                    today.setHours(0,0,0,0);
                    endDate.setHours(0,0,0,0);
                    const isOverdue = endDate < today;
                    
                    return (
                      <div 
                        key={r.id || idx}
                        onClick={() => quickReturn(r.id, r.customer_name)}
                        className={`p-4 cursor-pointer transition-colors flex items-center justify-between group ${
                          isOverdue ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <Avatar className={`h-12 w-12 border-2 ${isOverdue ? 'border-red-300' : 'border-slate-200'}`}>
                            <AvatarFallback className={`font-bold ${isOverdue ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>
                              {r.customer_name?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-bold text-slate-900">{r.customer_name}</p>
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <Package className="h-3 w-3" />
                              <span>{r.pending_items?.length || r.items?.filter(i => !i.returned).length || '?'} artículos</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className={`${
                            isOverdue 
                              ? 'bg-red-100 text-red-700 border-red-300' 
                              : 'bg-blue-500/10 text-blue-700 border-blue-200'
                          }`}>
                            {isOverdue ? '⚠️ ATRASADO' : formatDate(r.end_date)}
                          </Badge>
                          <ArrowRight className="h-5 w-5 text-slate-300 group-hover:text-slate-500 transition-colors" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-8 text-center text-slate-600">
                  <Calendar className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">Sin otras devoluciones programadas</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* ============ SECCIÓN DE SEGURIDAD: HISTORIAL DE DEVOLUCIONES RECIENTES ============ */}
        <div className="mt-12 pt-8 border-t-2 border-dashed border-slate-200">
          <Card className="border-2 border-amber-200 shadow-lg overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-bold flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  HISTORIAL DE DEVOLUCIONES RECIENTES
                  <Badge variant="outline" className="bg-white/20 text-white border-white/30 text-xs ml-2">
                    Sesión Actual
                  </Badge>
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-white hover:bg-white/20"
                    onClick={loadRecentReturns}
                    disabled={loadingRecentReturns}
                  >
                    <RefreshCcw className={`h-4 w-4 ${loadingRecentReturns ? 'animate-spin' : ''}`} />
                  </Button>
                  <Badge className="bg-white text-amber-700 font-bold text-lg px-3">
                    {recentReturns.length}
                  </Badge>
                </div>
              </div>
              <p className="text-amber-100 text-sm mt-1">
                Últimas 12 horas • Permite deshacer devoluciones si los artículos siguen disponibles
              </p>
            </CardHeader>
            <CardContent className="p-0">
              {loadingRecentReturns ? (
                <div className="p-8 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-amber-500" />
                  <p className="mt-2 text-slate-500">Cargando historial...</p>
                </div>
              ) : recentReturns.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Hora</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">ID Alquiler</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Cliente</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Artículos</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Movim. Caja</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {recentReturns.map((r) => (
                        <tr 
                          key={r.id} 
                          className={`hover:bg-amber-50/50 transition-colors ${r.has_cash_movement ? 'bg-amber-50/30' : ''}`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex flex-col">
                              <span className="font-medium text-slate-900">{formatReturnTime(r.actual_return_date)}</span>
                              <span className="text-xs text-slate-500">{formatReturnDate(r.actual_return_date)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <code className="bg-slate-100 px-2 py-1 rounded text-xs font-mono">
                              #{r.id?.substring(0, 8)}
                            </code>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Avatar className="h-8 w-8 border border-slate-200">
                                <AvatarFallback className="bg-slate-100 text-slate-600 text-xs">
                                  {r.customer_name?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="font-medium text-slate-900">{r.customer_name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <Package className="h-4 w-4 text-slate-600" />
                              <span className="text-slate-700">{r.items?.length || 0} items</span>
                              <span className="text-xs text-slate-600 truncate max-w-[120px]" title={r.items?.map(i => i.item_type).join(', ')}>
                                ({r.items?.slice(0, 2).map(i => i.item_type).join(', ')}{r.items?.length > 2 ? '...' : ''})
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {r.has_cash_movement ? (
                              <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                                <DollarSign className="h-3 w-3 mr-1" />
                                {formatCurrency(r.cash_movement_total)}
                              </Badge>
                            ) : (
                              <span className="text-slate-600 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                              onClick={() => handleUndoReturn(r)}
                              disabled={undoingReturn === r.id}
                            >
                              {undoingReturn === r.id ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                              ) : (
                                <RotateCcw className="h-4 w-4 mr-1" />
                              )}
                              <span className="hidden sm:inline">⚠️ Deshacer</span>
                              <span className="sm:hidden">⚠️</span>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-slate-600">
                  <CheckCheck className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">Sin devoluciones recientes</p>
                  <p className="text-sm mt-1">Las devoluciones de las últimas 12 horas aparecerán aquí</p>
                </div>
              )}
            </CardContent>
          </Card>
          
          <p className="mt-3 text-xs text-slate-600 text-center">
            ⚠️ Solo se pueden deshacer devoluciones si los artículos siguen disponibles (no re-alquilados)
          </p>
        </div>

        {/* HORA DE GRACIA - Configuración */}
        <div className="mt-8 flex items-center justify-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 max-w-lg mx-auto" data-testid="grace-hour-control">
          <Clock className="h-4 w-4 text-amber-600 flex-shrink-0" />
          <span className="text-sm text-amber-800 font-medium">Devolución matutina sin recargo hasta las</span>
          <select 
            value={graceHour}
            onChange={(e) => saveGraceHour(e.target.value)}
            disabled={savingGraceHour}
            className="text-sm font-bold text-amber-900 bg-white border border-amber-300 rounded-lg px-2 py-1 cursor-pointer focus:ring-2 focus:ring-amber-400 focus:outline-none"
            data-testid="grace-hour-select"
          >
            {["08:00","08:30","09:00","09:30","10:00","10:30","11:00","11:30","12:00"].map(h => (
              <option key={h} value={h}>{h}h</option>
            ))}
          </select>
          {savingGraceHour && <Loader2 className="h-4 w-4 animate-spin text-amber-600" />}
        </div>
      </div>

      {/* ============ DIALOG: CONFIRMAR DESHACER CON MOVIMIENTO DE CAJA ============ */}
      <Dialog open={showUndoConfirmDialog} onOpenChange={setShowUndoConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
              Atención: Movimientos de Caja
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 font-medium">
                Esta devolución implicó movimientos de dinero:
              </p>
              {pendingUndoRental && (
                <div className="mt-2 flex items-center gap-2">
                  <Badge className="bg-amber-200 text-amber-900">
                    {formatCurrency(pendingUndoRental.cash_movement_total)}
                  </Badge>
                  <span className="text-sm text-amber-700">
                    ({pendingUndoRental.cash_movements_count} movimiento{pendingUndoRental.cash_movements_count > 1 ? 's' : ''})
                  </span>
                </div>
              )}
            </div>
            
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">
                <strong>⚠️ Importante:</strong> Recuerda ajustar el dinero en caja manualmente si es necesario 
                (devolver el efectivo recibido, anular el cargo en tarjeta, etc.)
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={cancelUndoConfirm}>
              Cancelar
            </Button>
            <Button 
              variant="destructive"
              onClick={confirmUndoWithCashWarning}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Entendido, Deshacer Devolución
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={showRefundDialog} onOpenChange={setShowRefundDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="h-5 w-5 text-orange-600" />
              Reembolso Parcial
            </DialogTitle>
            <DialogDescription>
              Devuelve el importe por días no disfrutados
            </DialogDescription>
          </DialogHeader>
          
          {rental && (
            <div className="space-y-4 py-4">
              {/* Rental Info */}
              <div className="p-4 rounded-xl bg-slate-50">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-600">Cliente</span>
                  <span className="font-semibold">{rental.customer_name}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-600">Días contratados</span>
                  <span className="font-semibold">{rental.days} días</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-600">Importe total</span>
                  <span className="font-semibold">{formatCurrency(rental.total_amount)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Ya pagado</span>
                  <span className="font-semibold text-emerald-600">{formatCurrency(rental.paid_amount)}</span>
                </div>
              </div>

              {/* Days to refund */}
              <div>
                <Label>Días a reembolsar (máx. {maxRefundDays})</Label>
                <div className="flex items-center gap-3 mt-2">
                  <Input
                    type="number"
                    min="1"
                    max={maxRefundDays}
                    value={refundDays}
                    onChange={(e) => handleRefundDaysChange(e.target.value)}
                    className="h-12 w-24 text-xl text-center font-bold"
                  />
                  <div className="flex gap-1">
                    {[1, 2, 3].filter(d => d <= maxRefundDays).map(d => (
                      <Button
                        key={d}
                        variant={refundDays === d ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleRefundDaysChange(d)}
                      >
                        {d}d
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Refund Amount */}
              <div>
                <Label>Importe a devolver</Label>
                <div className="relative mt-2">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg font-semibold text-slate-500">{getCurrencySymbol()}</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={refundAmount.toFixed(2)}
                    onChange={(e) => {
                      // Allow manual override but cap at paid_amount (hard cap in UI too)
                      const v = parseFloat(e.target.value) || 0;
                      const cap = rental.paid_amount || rental.total_amount || 0;
                      setRefundAmount(parseFloat(Math.min(Math.max(0, v), cap).toFixed(2)));
                    }}
                    className="h-12 pl-8 text-xl font-bold text-orange-600"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Calculado: pagó {formatCurrency((rental.paid_amount || rental.total_amount || 0))} · {refundDays} día(s) a reembolsar · máx. {formatCurrency((rental.paid_amount || rental.total_amount || 0))}
                </p>
              </div>

              {/* Payment Method - LOCKED for security */}
              <div>
                <Label>Método de devolución</Label>
                <div className="mt-1 h-11 px-3 flex items-center justify-between rounded-md border border-slate-300 bg-slate-100 cursor-not-allowed">
                  <div className="flex items-center gap-2">
                    {refundMethod === 'pending' ? (
                      <>
                        <Clock className="h-4 w-4 text-amber-600" />
                        <span className="font-medium text-slate-700">Pendiente (se cancela deuda)</span>
                      </>
                    ) : refundMethod === 'cash' ? (
                      <>
                        <Banknote className="h-4 w-4 text-emerald-600" />
                        <span className="font-medium text-slate-700">Efectivo</span>
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-4 w-4 text-blue-600" />
                        <span className="font-medium text-slate-700">Tarjeta</span>
                      </>
                    )}
                  </div>
                  <Lock className="h-4 w-4 text-slate-600" />
                </div>
                <div className="mt-2 p-2 rounded-lg bg-amber-500/10 border border-amber-200">
                  <p className="text-xs text-amber-800 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3 flex-shrink-0" />
                    <span>Por seguridad, la devolución se realiza al mismo método de pago original.</span>
                  </p>
                </div>
              </div>

              {/* Reason */}
              <div>
                <Label>Motivo (opcional)</Label>
                <Textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="Ej: Devuelve antes por mal tiempo..."
                  className="mt-1"
                  rows={2}
                />
              </div>

              {/* Summary */}
              <div className="p-4 rounded-xl bg-orange-50 border border-orange-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-700">Reembolso a realizar</p>
                    <p className="text-2xl font-bold text-orange-700">{formatCurrency(refundAmount)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-orange-700">Nuevo período</p>
                    <p className="text-lg font-semibold text-orange-800">{rental.days - refundDays} días</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRefundDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={processRefund} 
              disabled={processingRefund || refundAmount <= 0}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {processingRefund ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Banknote className="h-4 w-4 mr-2" />
              )}
              Confirmar Reembolso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Info Modal */}
      <Dialog open={showCustomerModal} onOpenChange={setShowCustomerModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              Ficha del Cliente
            </DialogTitle>
            <DialogDescription>
              Información de contacto y material pendiente
            </DialogDescription>
          </DialogHeader>
          
          {customerLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : selectedCustomer && (
            <div className="space-y-4 py-4">
              {/* Customer Name & Status */}
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">{selectedCustomer.name}</h3>
                    {selectedCustomer.days_overdue > 0 && (
                      <Badge className="bg-red-100 text-red-700 border-red-200 mt-2">
                        ⚠️ Retrasado {selectedCustomer.days_overdue} {selectedCustomer.days_overdue === 1 ? 'día' : 'días'}
                      </Badge>
                    )}
                  </div>
                  {selectedCustomer.dni && (
                    <div className="text-right">
                      <p className="text-xs text-slate-500">DNI/Pasaporte</p>
                      <p className="font-mono font-semibold text-slate-700">{selectedCustomer.dni}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-3">
                {/* Phone */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-500/10 border border-emerald-200">
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-emerald-600" />
                    <div>
                      <p className="text-xs text-emerald-600 font-medium">Teléfono</p>
                      <p className="font-semibold text-slate-900">{selectedCustomer.phone || 'No registrado'}</p>
                    </div>
                  </div>
                  {selectedCustomer.phone && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-500/20"
                        onClick={() => callPhone(selectedCustomer.phone)}
                      >
                        <Phone className="h-3 w-3" />
                        Llamar
                      </Button>
                      <Button
                        size="sm"
                        className="gap-1 bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => sendWhatsAppMessage(selectedCustomer.phone, selectedCustomer.name)}
                        data-testid="whatsapp-btn"
                      >
                        <MessageCircle className="h-3 w-3" />
                        WhatsApp
                      </Button>
                    </div>
                  )}
                </div>

                {/* Email */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-blue-500/10 border border-blue-200">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="text-xs text-blue-600 font-medium">Email</p>
                      <p className="font-semibold text-slate-900">{selectedCustomer.email || 'No registrado'}</p>
                    </div>
                  </div>
                  {selectedCustomer.email && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1 border-blue-300 text-blue-700 hover:bg-blue-500/20"
                      onClick={() => sendEmail(selectedCustomer.email, selectedCustomer.name)}
                    >
                      <Mail className="h-3 w-3" />
                      Enviar Email
                    </Button>
                  )}
                </div>

                {/* Hotel/Address */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-purple-500/10 border border-purple-200">
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-purple-600" />
                    <div>
                      <p className="text-xs text-purple-600 font-medium">Hotel / Alojamiento</p>
                      <p className="font-semibold text-slate-900">{selectedCustomer.hotel || 'No registrado'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pending Items */}
              {selectedCustomer.pending_items && selectedCustomer.pending_items.length > 0 && (
                <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Package className="h-5 w-5 text-amber-600" />
                    <p className="font-semibold text-amber-800">Material Pendiente de Devolver</p>
                  </div>
                  <div className="space-y-2">
                    {selectedCustomer.pending_items.map((item, index) => (
                      <div 
                        key={item.barcode || item.id || index}
                        className="flex items-center justify-between p-2 rounded-lg bg-white border border-amber-200"
                      >
                        <div>
                          <p className="font-medium text-slate-900">{item.brand} {item.model}</p>
                          <p className="text-xs text-slate-500">
                            {item.item_type} • Talla {item.size}
                            <span className="font-mono ml-2">#{item.internal_code || item.barcode}</span>
                          </p>
                        </div>
                        <Badge variant="outline" className="text-amber-600 border-amber-300">
                          Pendiente
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedCustomer.notes && (
                <div className="p-3 rounded-lg bg-slate-100 border border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">Observaciones</p>
                  <p className="text-sm text-slate-700">{selectedCustomer.notes}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomerModal(false)}>
              Cerrar
            </Button>
            {selectedCustomer?.phone && (
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={() => sendWhatsAppMessage(selectedCustomer.phone, selectedCustomer.name)}
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Contactar por WhatsApp
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change/Extension Modal */}
      {/* ============ MULTI-ITEM CHANGE/EXTENSION MODAL ============ */}
      <Dialog open={changeModal} onOpenChange={closeChangeModal}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <ArrowLeftRight className="h-6 w-6 text-orange-500" />
              Gestión de Contrato Completo
            </DialogTitle>
            <DialogDescription>
              Gestiona todos los artículos del cliente - Cambios de material y ajuste de fecha
            </DialogDescription>
          </DialogHeader>
          
          {changeRental && !changeComplete && (
            <div className="space-y-4 py-4">
              {/* Customer Info Header */}
              <div className="p-4 rounded-xl bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-lg font-bold text-slate-900">{changeRental.customer_name}</p>
                    <p className="text-sm text-slate-500">{changeRental.customer_dni}</p>
                  </div>
                  <div className="text-right">
                    <Badge className="bg-blue-600 text-white border-0 mb-1">
                      {changeDaysRemaining} días restantes
                    </Badge>
                    <p className="text-xs text-slate-500">Total: {formatCurrency(changeRental.total_amount || 0)}</p>
                  </div>
                </div>
              </div>

              {/* ALL ITEMS FROM CONTRACT */}
              <div className="space-y-2">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Package className="h-5 w-5 text-slate-500" />
                  Artículos del Contrato ({changeItems.length})
                </Label>
                
                <div className="space-y-2 max-h-64 overflow-y-auto p-2 bg-slate-50 rounded-lg">
                  {changeItems.map((item, index) => (
                    <div 
                      key={item.barcode || index}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        item.isSwapping 
                          ? 'bg-orange-50 border-orange-300' 
                          : activeSwapIndex === index
                          ? 'bg-blue-500/10 border-blue-300'
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-sm">
                              {item.internal_code || item.barcode}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {item.item_type}
                            </Badge>
                            {item.size && (
                              <span className="text-xs text-slate-500">Talla {item.size}</span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            {item.brand} {item.model}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {item.isSwapping ? (
                            <>
                              <div className="text-right mr-2">
                                <p className="text-xs text-orange-600 font-medium">→ {item.swapNewItem?.internal_code || item.swapNewItem?.barcode}</p>
                                <p className="text-xs text-slate-500">
                                  Delta: {item.swapDelta >= 0 ? '+' : ''}{formatCurrency(item.swapDelta)}
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => cancelItemSwap(index)}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          ) : activeSwapIndex === index ? (
                            <div className="flex gap-2 items-center">
                              <Input
                                ref={changeInputRef}
                                value={changeNewBarcode}
                                onChange={(e) => setChangeNewBarcode(e.target.value.toUpperCase())}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault(); // Prevent form submission
                                    if (changeNewBarcode.trim() && !changeLoading) {
                                      searchSwapItem(changeNewBarcode);
                                    }
                                  } else if (e.key === 'Escape') {
                                    setActiveSwapIndex(null);
                                  }
                                }}
                                placeholder="Escanear código..."
                                className="h-8 w-40 text-sm font-mono"
                                autoFocus
                              />
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => {
                                  if (changeNewBarcode.trim()) {
                                    searchSwapItem(changeNewBarcode);
                                  }
                                }}
                                disabled={!changeNewBarcode.trim() || changeLoading}
                                className="h-8 px-2 bg-blue-600 hover:bg-blue-700"
                              >
                                {changeLoading ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Check className="h-3 w-3" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setActiveSwapIndex(null)}
                                className="h-8 w-8 p-0"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => startItemSwap(index)}
                              className="gap-1 text-orange-600 border-orange-200 hover:bg-orange-50"
                            >
                              <Scan className="h-3 w-3" />
                              Sustituir
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* DATE ADJUSTMENT - Allows both extension and early return */}
              <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CalendarPlus className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-slate-900">Ajuste de Fecha</p>
                      <p className="text-xs text-slate-500">Extensión o devolución anticipada</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      variant={changeAdjustDate ? "default" : "outline"}
                      size="sm"
                      onClick={() => setChangeAdjustDate(!changeAdjustDate)}
                      className={changeAdjustDate ? "bg-blue-600" : ""}
                    >
                      {changeAdjustDate ? 'Activado' : 'Activar'}
                    </Button>
                  </div>
                </div>
                
                {changeAdjustDate && (
                  <div className="mt-4 space-y-4">
                    {/* Date picker and info */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-slate-600">Fecha fin original</Label>
                        <p className="text-lg font-semibold text-slate-700">
                          {changeRental?.end_date ? new Date(changeRental.end_date).toLocaleDateString('es-ES') : '-'}
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm text-slate-600">Nueva fecha de fin</Label>
                        <Input
                          type="date"
                          value={changeNewEndDate}
                          onChange={(e) => handleDateAdjustment(e.target.value)}
                          className="h-10 font-semibold"
                        />
                      </div>
                    </div>
                    
                    {/* Clear visualization of days */}
                    <div className="p-3 rounded-lg bg-white border border-blue-200">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-xs text-slate-500 uppercase font-medium">Días Originales</p>
                          <p className="text-2xl font-bold text-slate-700">{changeOriginalDays}</p>
                        </div>
                        <div className="flex items-center justify-center">
                          <ArrowRight className="h-6 w-6 text-slate-600" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase font-medium">Días Nuevos</p>
                          <p className={`text-2xl font-bold ${
                            changeNewTotalDays > changeOriginalDays ? 'text-orange-600' :
                            changeNewTotalDays < changeOriginalDays ? 'text-emerald-600' : 'text-slate-700'
                          }`}>{changeNewTotalDays}</p>
                        </div>
                      </div>
                      
                      {/* Economic difference */}
                      {changeDateDelta !== 0 && (
                        <div className={`mt-3 p-2 rounded text-center ${
                          changeDateDelta > 0 ? 'bg-orange-100' : 'bg-emerald-500/20'
                        }`}>
                          <p className="text-xs text-slate-600">
                            {changeDateDelta > 0 ? 'Suplemento por extensión' : 'Abono por devolución anticipada'}
                          </p>
                          <p className={`text-lg font-bold ${
                            changeDateDelta > 0 ? 'text-orange-700' : 'text-emerald-700'
                          }`}>
                            {changeDateDelta > 0 ? '+' : ''}{formatCurrency(changeDateDelta)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* TOTAL DELTA SUMMARY */}
              <div className={`p-5 rounded-xl border-2 ${
                changeTotalDelta > 0 
                  ? 'bg-orange-50 border-orange-300' 
                  : changeTotalDelta < 0 
                  ? 'bg-emerald-500/10 border-emerald-300'
                  : 'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">
                      {changeTotalDelta > 0 ? '⬆️ TOTAL A COBRAR' : 
                       changeTotalDelta < 0 ? '⬇️ TOTAL A ABONAR' : 
                       '↔️ SIN DIFERENCIA'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {changeItems.filter(i => i.isSwapping).length > 0 && 
                        `${changeItems.filter(i => i.isSwapping).length} cambio(s) de material`}
                      {changeItems.filter(i => i.isSwapping).length > 0 && changeAdjustDate && changeDateDelta !== 0 && ' + '}
                      {changeAdjustDate && changeDateDelta !== 0 && 
                        (changeDateDelta > 0 ? 'extensión de fecha' : 'devolución anticipada')}
                    </p>
                  </div>
                  <p className={`text-3xl font-bold ${
                    changeTotalDelta > 0 ? 'text-orange-600' : 
                    changeTotalDelta < 0 ? 'text-emerald-600' : 'text-slate-500'
                  }`}>
                    {changeTotalDelta > 0 ? '+' : changeTotalDelta < 0 ? '-' : ''}{formatCurrency(Math.abs(changeTotalDelta))}
                  </p>
                </div>
              </div>

              {/* Payment Method - Only if there's a delta */}
              {changeTotalDelta !== 0 && (
                <div className="space-y-2">
                  <Label className="shrink-0">Método de {changeTotalDelta > 0 ? 'cobro' : 'abono'}:</Label>
                  
                  {/* LOCKED: Siempre al método de pago original para evitar descuadres */}
                  {changeTotalDelta > 0 ? (
                    <>
                      <div className="h-10 px-3 flex items-center justify-between rounded-md border border-slate-300 bg-slate-100 cursor-not-allowed max-w-xs">
                        <div className="flex items-center gap-2">
                          {(changeRental?.payment_method || 'cash') === 'pending' ? (
                            <>
                              <Clock className="h-4 w-4 text-amber-600" />
                              <span className="font-medium text-slate-700">Pendiente (se añade a deuda)</span>
                            </>
                          ) : (changeRental?.payment_method || 'cash') === 'card' ? (
                            <>
                              <CreditCard className="h-4 w-4 text-blue-600" />
                              <span className="font-medium text-slate-700">Tarjeta</span>
                            </>
                          ) : (
                            <>
                              <Banknote className="h-4 w-4 text-emerald-600" />
                              <span className="font-medium text-slate-700">Efectivo</span>
                            </>
                          )}
                        </div>
                        <Lock className="h-4 w-4 text-slate-600" />
                      </div>
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3 flex-shrink-0" />
                        <span>El cobro se realiza por el mismo método de pago original.</span>
                      </p>
                    </>
                  ) : (
                    /* LOCKED for refunds/abonos */
                    <>
                      <div className="h-10 px-3 flex items-center justify-between rounded-md border border-slate-300 bg-slate-100 cursor-not-allowed max-w-xs">
                        <div className="flex items-center gap-2">
                          {(changeRental?.payment_method || 'cash') === 'pending' ? (
                            <>
                              <Clock className="h-4 w-4 text-amber-600" />
                              <span className="font-medium text-slate-700">Pendiente (se cancela deuda)</span>
                            </>
                          ) : (changeRental?.payment_method || 'cash') === 'card' ? (
                            <>
                              <CreditCard className="h-4 w-4 text-blue-600" />
                              <span className="font-medium text-slate-700">Tarjeta</span>
                            </>
                          ) : (
                            <>
                              <Banknote className="h-4 w-4 text-emerald-600" />
                              <span className="font-medium text-slate-700">Efectivo</span>
                            </>
                          )}
                        </div>
                        <Lock className="h-4 w-4 text-slate-600" />
                      </div>
                      <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-200 max-w-md">
                        <p className="text-xs text-amber-800 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3 flex-shrink-0" />
                          <span>Por seguridad, el abono se realiza al mismo método de pago original.</span>
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Success State */}
          {changeComplete && (
            <div className="py-8 text-center">
              <CheckCircle className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                ¡Cambios Procesados!
              </h3>
              <p className="text-slate-600 mb-6">
                {changeItems.filter(i => i.isSwapping).length > 0 && 
                  `${changeItems.filter(i => i.isSwapping).length} artículo(s) sustituido(s)`}
                {changeItems.filter(i => i.isSwapping).length > 0 && changeAdjustDate && changeDateDelta !== 0 && ' + '}
                {changeAdjustDate && changeDateDelta !== 0 && 'ajuste de fecha aplicado'}
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={printMultiChangeTicket}
                  className="gap-2"
                >
                  <Printer className="h-4 w-4" />
                  Imprimir Ticket
                </Button>
                <Button onClick={closeChangeModal}>
                  Cerrar
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            {!changeComplete && (
              <>
                <Button variant="outline" onClick={closeChangeModal}>
                  Cancelar
                </Button>
                <Button 
                  onClick={executeAllChanges}
                  disabled={changeLoading || (changeItems.filter(i => i.isSwapping).length === 0 && (!changeAdjustDate || changeDateDelta === 0))}
                  className={`min-w-[180px] ${
                    changeTotalDelta > 0 ? 'bg-orange-600 hover:bg-orange-700' :
                    changeTotalDelta < 0 ? 'bg-emerald-600 hover:bg-emerald-700' :
                    'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {changeLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Zap className="h-4 w-4 mr-2" />
                  )}
                  {changeTotalDelta > 0 ? `Cobrar ${formatCurrency(changeTotalDelta)}` :
                   changeTotalDelta < 0 ? `Abonar ${formatCurrency(Math.abs(changeTotalDelta))}` :
                   'Procesar Cambios'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ MODAL DE LIQUIDACIÓN (Settlement) ============ */}
      <Dialog open={showSettlementModal} onOpenChange={setShowSettlementModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              {settlementData.balance > 0 ? (
                <>
                  <AlertTriangle className="h-6 w-6 text-orange-500" />
                  Saldo Pendiente a Cobrar
                </>
              ) : settlementData.balance < 0 ? (
                <>
                  <ArrowDownToLine className="h-6 w-6 text-emerald-500" />
                  Reembolso al Cliente
                </>
              ) : (
                <>
                  <CheckCircle className="h-6 w-6 text-blue-500" />
                  Confirmar Devolución
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {settlementData.balanceReason}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* ⚠️ OPCIÓN DE ANULACIÓN TOTAL / DEVOLUCIÓN COMPLETA */}
            <div className={`rounded-lg p-4 border-2 transition-all ${
              fullRefundOverride 
                ? 'bg-red-50 border-red-300' 
                : 'bg-slate-50 border-slate-200'
            }`}>
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="fullRefundOverride"
                  checked={fullRefundOverride}
                  onChange={(e) => setFullRefundOverride(e.target.checked)}
                  className="mt-1 h-5 w-5 rounded border-red-300 text-red-600 focus:ring-red-500"
                />
                <div className="flex-1">
                  <label 
                    htmlFor="fullRefundOverride" 
                    className={`font-semibold cursor-pointer ${fullRefundOverride ? 'text-red-700' : 'text-slate-700'}`}
                  >
                    ⚠️ Anulación Total / Devolución Completa
                  </label>
                  <p className={`text-xs mt-1 ${fullRefundOverride ? 'text-red-600' : 'text-slate-500'}`}>
                    Activa esta opción para casos excepcionales (cierre de estación, fuerza mayor). 
                    Se ignorará el cobro mínimo de 1 día y se devolverá TODO el importe pagado.
                  </p>
                  {fullRefundOverride && (
                    <div className="mt-2 p-2 rounded bg-red-100 border border-red-200">
                      <p className="text-sm font-bold text-red-700">
                        ¡ATENCIÓN! Se devolverá: {formatCurrency(settlementData.totalPaid || 0)} al cliente
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        El alquiler quedará registrado con saldo final {formatCurrency(0)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Resumen del Cálculo - Se muestra diferente si hay override */}
            {!fullRefundOverride ? (
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Días contratados:</span>
                  <span className="font-medium">{settlementData.daysPaid} días</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Días usados:</span>
                  <span className={`font-medium ${settlementData.daysUsed > settlementData.daysPaid ? 'text-orange-600' : settlementData.daysUsed < settlementData.daysPaid ? 'text-emerald-600' : ''}`}>
                    {settlementData.daysUsed} días
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Precio/día:</span>
                  <span className="font-medium">{formatCurrency(settlementData.pricePerDay)}</span>
                </div>
                <Separator className="my-2" />
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">
                    {settlementData.isFullyPendingRental ? 'Deuda inicial:' : 'Pagado inicialmente:'}
                  </span>
                  <span className={`font-medium ${settlementData.isFullyPendingRental ? 'text-red-600' : ''}`}>
                    {settlementData.isFullyPendingRental 
                      ? `${formatCurrency(settlementData.pendingAmount)} (pendiente)`
                      : `${formatCurrency(settlementData.totalPaid)}`
                    }
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Valor servicio usado:</span>
                  <span className="font-medium">{formatCurrency(settlementData.serviceUsed)}</span>
                </div>
                {settlementData.pendingRefund > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-amber-600">Crédito pendiente (días descontados):</span>
                    <span className="font-medium text-amber-600">-{formatCurrency(settlementData.pendingRefund)}</span>
                  </div>
                )}

                {/* ── Desglose por artículo / pack ──────────────────────────── */}
                {settlementData.itemBreakdowns?.length > 0 && (
                  <>
                    <Separator className="my-2" />
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Desglose por artículo</p>
                    <div className="space-y-1.5">
                      {settlementData.itemBreakdowns.map((bd, idx) => {
                        const isOverdue = bd.type === 'overdue';
                        const isEarly   = bd.type === 'early';
                        const isPending = bd.type === 'pending';
                        return (
                          <div key={bd.barcode || bd.label || idx} className={`flex items-center justify-between rounded-md px-3 py-1.5 text-sm ${
                            isOverdue ? 'bg-orange-50 border border-orange-200' :
                            isEarly   ? 'bg-emerald-50 border border-emerald-200' :
                            isPending ? 'bg-amber-50 border border-amber-200' :
                                        'bg-slate-100 border border-slate-200'
                          }`}>
                            <div className="min-w-0 flex-1">
                              <span className={`font-medium truncate block ${
                                isOverdue ? 'text-orange-800' : isEarly ? 'text-emerald-800' : isPending ? 'text-amber-800' : 'text-slate-600'
                              }`}>{bd.label}</span>
                              <span className="text-xs text-slate-500">
                                {isOverdue && `${bd.daysPaid}d contratados + ${bd.daysOverdue}d extra = ${bd.daysUsed}d usados`}
                                {isEarly   && `${bd.daysPaid}d contratados → ${bd.daysUsed}d usados (anticipada)`}
                                {isPending && `Saldo pendiente`}
                                {bd.type === 'ontime' && `${bd.daysPaid}d — en fecha`}
                              </span>
                            </div>
                            <span className={`font-bold text-sm ml-3 whitespace-nowrap ${
                              isOverdue ? 'text-orange-700' : isEarly ? 'text-emerald-700' : isPending ? 'text-amber-700' : 'text-slate-500'
                            }`}>
                              {bd.amount > 0 ? `+${formatCurrency(bd.amount)}` : bd.amount < 0 ? `-${formatCurrency(Math.abs(bd.amount))}` : formatCurrency(0)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="bg-red-50 rounded-lg p-4 space-y-2 border border-red-200">
                <div className="flex justify-between text-sm">
                  <span className="text-red-700">Pagado por el cliente:</span>
                  <span className="font-bold text-red-700">{formatCurrency(settlementData.totalPaid)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-red-700 line-through">Valor servicio usado:</span>
                  <span className="font-medium text-red-400 line-through">{formatCurrency(settlementData.serviceUsed)}</span>
                </div>
                <Separator className="my-2 bg-red-200" />
                <div className="flex justify-between text-sm">
                  <span className="text-red-800 font-semibold">Coste anulado ({formatCurrency(0)}):</span>
                  <span className="font-bold text-red-800">EXCEPCIÓN APLICADA</span>
                </div>
              </div>
            )}

            {/* Resultado Final */}
            <div className={`rounded-lg p-4 text-center ${
              fullRefundOverride ? 'bg-red-100 border-2 border-red-300' :
              settlementData.balance > 0 ? 'bg-orange-50 border-2 border-orange-200' :
              settlementData.balance < 0 ? 'bg-emerald-500/10 border-2 border-emerald-200' :
              'bg-blue-500/10 border-2 border-blue-200'
            }`}>
              <p className="text-sm text-slate-600 mb-1">
                {fullRefundOverride ? '⚠️ DEVOLUCIÓN TOTAL (Excepción):' :
                 settlementData.balance > 0 ? 'El cliente debe pagar:' :
                 settlementData.balance < 0 ? 'Importe a devolver:' : 'Saldo:'}
              </p>
              <p className={`text-3xl font-bold ${
                fullRefundOverride ? 'text-red-600' :
                settlementData.balance > 0 ? 'text-orange-600' :
                settlementData.balance < 0 ? 'text-emerald-600' :
                'text-blue-600'
              }`}>
                {fullRefundOverride 
                  ? `-${formatCurrency(settlementData.totalPaid || 0)}`
                  : `${settlementData.balance > 0 ? '+' : ''}${formatCurrency(Math.abs(settlementData.balance))}`
                }
              </p>
            </div>

            {/* Selector de Método de Pago */}
            {settlementData.balance !== 0 && (
              <div className="space-y-2">
                <Label>Método de {settlementData.balance > 0 ? 'cobro' : 'reembolso'}</Label>
                
                {/* COBRO (balance > 0): SIEMPRE selector libre */}
                {settlementData.balance > 0 ? (
                  <>
                    <MixedPaymentInput
                      total={settlementData.balance}
                      onChange={(state) => {
                        setSettlementMixedPayment(state);
                        setSettlementData(prev => ({...prev, paymentMethod: state.method}));
                      }}
                    />
                    <p className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                      <AlertTriangle className="h-3 w-3" />
                      El cajero puede elegir libremente el método de cobro del recargo
                    </p>
                  </>
                ) : settlementData.balance < 0 ? (
                  /* REEMBOLSO (balance < 0): BLOQUEADO al método original */
                  <>
                    <div className="h-10 px-3 flex items-center justify-between rounded-md border border-slate-300 bg-slate-100 cursor-not-allowed">
                      <div className="flex items-center gap-2">
                        {settlementData.originalPaymentMethod === 'card' ? (
                          <>
                            <CreditCard className="h-4 w-4 text-blue-600" />
                            <span className="font-medium text-slate-700">Tarjeta</span>
                          </>
                        ) : (
                          <>
                            <Banknote className="h-4 w-4 text-emerald-600" />
                            <span className="font-medium text-slate-700">Efectivo</span>
                          </>
                        )}
                      </div>
                      <Lock className="h-4 w-4 text-slate-600" />
                    </div>
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      El reembolso se realiza siempre por el mismo método de pago original
                    </p>
                  </>
                ) : (
                  /* SIN COBRO NI REEMBOLSO (balance === 0) */
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    Sin movimiento de pago necesario
                  </p>
                )}
              </div>
            )}

            {/* Items a devolver */}
            <div className="text-sm text-slate-500">
              <span className="font-medium">{settlementData.itemsToReturn?.reduce((sum, barcode) => {
                const item = rental?.items.find(i => i.barcode === barcode);
                return sum + (item?.quantity || 1);
              }, 0) || 0} unidad(es)</span> serán marcados como devueltos
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => {
              setShowSettlementModal(false);
              setFullRefundOverride(false); // Reset on close
            }}>
              Cancelar
            </Button>
            <Button
              onClick={confirmSettlement}
              disabled={settlementProcessing}
              className={`min-w-[160px] ${
                fullRefundOverride ? 'bg-red-600 hover:bg-red-700' :
                settlementData.balance > 0 ? 'bg-orange-600 hover:bg-orange-700' :
                settlementData.balance < 0 ? 'bg-emerald-600 hover:bg-emerald-700' :
                'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {settlementProcessing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : fullRefundOverride ? (
                <AlertTriangle className="h-4 w-4 mr-2" />
              ) : settlementData.balance > 0 ? (
                <CreditCard className="h-4 w-4 mr-2" />
              ) : settlementData.balance < 0 ? (
                <Banknote className="h-4 w-4 mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              {fullRefundOverride 
                ? `⚠️ ANULAR y Devolver ${formatCurrency(settlementData.totalPaid || 0)}`
                : settlementData.balance > 0 ? `Cobrar ${formatCurrency(settlementData.balance)}` 
                : settlementData.balance < 0 ? `Devolver ${formatCurrency(Math.abs(settlementData.balance))}` 
                : 'Confirmar Devolución'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ DIÁLOGO DE GESTIÓN DE DEPÓSITO ============ */}
      <Dialog open={showDepositDialog} onOpenChange={setShowDepositDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-amber-600" />
              Gestión de Depósito
            </DialogTitle>
            <DialogDescription>
              Este alquiler tiene un depósito de <span className="font-bold text-amber-600">{formatCurrency(rental?.deposit || 0)}</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex flex-col gap-3">
              <Button
                variant={depositAction === "return" ? "default" : "outline"}
                className="h-20 justify-start text-left"
                onClick={() => setDepositAction("return")}
              >
                <div className="flex items-start gap-3 w-full">
                  <CheckCircle className={`h-6 w-6 mt-1 ${depositAction === "return" ? "text-white" : "text-green-600"}`} />
                  <div className="flex-1">
                    <div className="font-semibold">Devolver Depósito</div>
                    <div className="text-sm opacity-80">Material en buen estado - Devolver {formatCurrency(rental?.deposit || 0)} al cliente</div>
                  </div>
                </div>
              </Button>
              
              <Button
                variant={depositAction === "forfeit" ? "default" : "outline"}
                className="h-20 justify-start text-left bg-red-50 hover:bg-red-100"
                onClick={() => setDepositAction("forfeit")}
              >
                <div className="flex items-start gap-3 w-full">
                  <AlertTriangle className={`h-6 w-6 mt-1 ${depositAction === "forfeit" ? "text-white" : "text-red-600"}`} />
                  <div className="flex-1">
                    <div className="font-semibold text-red-700">Incautar Depósito</div>
                    <div className="text-sm text-red-600">Material dañado/perdido - No devolver</div>
                  </div>
                </div>
              </Button>
            </div>
            
            {depositAction === "forfeit" && (
              <div className="space-y-2">
                <Label htmlFor="forfeit-reason">Motivo de incautación *</Label>
                <Textarea
                  id="forfeit-reason"
                  placeholder="Ej: Esquí roto, bota perdida, daños en el material..."
                  value={forfeitReason}
                  onChange={(e) => setForfeitReason(e.target.value)}
                  rows={3}
                  className="resize-none"
                />
              </div>
            )}
            
            <div className={`p-4 rounded-lg border ${
              depositAction === "return" 
                ? "bg-emerald-500/10 border-green-200" 
                : "bg-red-50 border-red-200"
            }`}>
              <div className="text-sm font-medium">
                {depositAction === "return" ? (
                  <>
                    <CheckCircle className="h-4 w-4 inline mr-2 text-green-600" />
                    Se registrará una <strong>SALIDA</strong> de {formatCurrency(rental?.deposit || 0)} en caja
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-4 w-4 inline mr-2 text-red-600" />
                    El depósito pasará a <strong>INGRESO EXTRA</strong> en reportes
                  </>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDepositDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={confirmDepositAndReturn}
              disabled={processing || (depositAction === "forfeit" && !forfeitReason.trim())}
            >
              {processing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {depositAction === "return" ? "Devolver Depósito y Completar" : "Incautar y Completar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ============ DATE WARNING MODAL ============ */}
      {/* Shown when an item's expected return date does not match today */}
      <Dialog open={dateWarningModal} onOpenChange={(open) => { if (!open) dismissDateWarning(); }}>
        <DialogContent className="sm:max-w-md" data-testid="date-warning-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Desajuste de Fecha de Devolución
            </DialogTitle>
            <DialogDescription>
              Verifica la fecha antes de continuar
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
              <p className="text-amber-900 font-medium text-sm leading-relaxed">
                Atención: La fecha de devolución esperada para{' '}
                <strong>{dateWarningData?.itemName || 'este artículo'}</strong> es el{' '}
                <strong>
                  {dateWarningData?.expectedDate?.toLocaleDateString('es-ES', {
                    weekday: 'long',
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                  })}
                </strong>
                , no hoy.
              </p>
              <p className="text-amber-700 text-xs mt-2">
                {dateWarningData?.isEarly
                  ? 'El cliente está devolviendo el material antes de la fecha prevista.'
                  : 'El cliente está devolviendo el material después de la fecha prevista.'}
              </p>
            </div>
            <p className="text-slate-600 text-sm text-center">
              ¿Deseas procesarlo de todos modos?
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={dismissDateWarning}
              data-testid="date-warning-cancel-btn"
            >
              <X className="h-4 w-4 mr-2" />
              Cancelar
            </Button>
            <Button
              onClick={confirmDateWarning}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="date-warning-confirm-btn"
            >
              <Check className="h-4 w-4 mr-2" />
              Continuar de todos modos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
